// ============================================================
// casevoApi.ts
// API client — now uses JWT token from the store for auth
// Token is attached to every request via axios interceptor
// ============================================================
import axios from 'axios'
import { markOwnBillWrite } from '../services/ownBillWrites'
import { publishLocalEntityChange } from '../services/localEntityChanges'
import { TAB_ID } from '../services/tabId'
import { Witness, TpiContact, RawCase, ViewingPaneToggles, CaseLinkSummary, CaseLinkRelationship } from '../types/case.types'
import { Client, CreateClientPayload, UpdateClientPayload } from '../types/client.types'
import { CalendarEntry, DayNote, DayNoteScope, Reminder, Appointment, CreateAppointmentPayload, UpdateAppointmentPayload } from '../types/calendar.types'
import { AppNotification } from '../types/notification.types'
import { DocumentSearchResult, type EsignedFileInfo } from '../types/documentSearch.types'
import type { Task, CreateTaskPayload, UpdateTaskPayload, ListTasksFilters, TaskSummary } from '../types/tasks.types'
import type { Activity, ActivityTotal, CreateActivityPayload, UpdateActivityPayload } from '../types/activity.types'
import type { MissingLimitationCase, OverdueDeadline, DeadlineType, CaseDeadline, CreateCaseDeadlinePayload, UpdateCaseDeadlinePayload, CreateDeadlineTypePayload, UpdateDeadlineTypePayload } from '../types/deadlines.types'
import type { SettlementState, PreviewEdits, PreviewOptions as SettlementPreviewOptions, PreviewResult, MatterValues, CfaEditable, BaseCostsOverride, InterimPayment, SettlementDisbursement, RecoveryStatus, SettlementDefaults, SettlementCardKind, SettlementCardSave, SettlementCardSaved } from '../types/settlement.types'
import type {
  CaseStatements, ClientApprovalBody, LaterInstructionBody, CourtOrderBody, CourtStage, DraftFields, LitigationFriendState, Representatives, SendRecipient, SettlementWordingPatch, SettlementWordingState,
  StatementDocument, StatementSendDraft, StatementStatus, RegisterPage, MismatchOverrideBody,
} from '../types/settlementStatement.types'
import type { GradeRate, UpdateGradeRatePayload } from '../types/grades.types'
import type { NotificationPreferences } from '../types/notifications.types'
import type { AuditLogEntry, PaginatedAuditLogs, Pagination } from '../types/auditLog.types'
import { FirmUser } from '../types/user.types'
import type { ClientNote, CreateClientNotePayload, UpdateClientNotePayload } from '../types/clientNote.types'
import type { PermissionKey, EffectivePermissions, UserPermissionOverrides } from '../constants/permissions'
import { Invite } from '../types/invite.types'
import type { ClaimRoute, ClaimType } from '../utils/claimTypes'
import type { ClaimTypeFilterValue, ClaimTypeOptions } from '../utils/claimTypeFilter'
// AuthUser is type-only — keeps this module's runtime imports free of
// the store (which imports the API helpers at the top of its file)
// so the dependency graph stays acyclic.
import type { AuthUser } from '../store/useCasevoStore'
import type { BillSignatories, FirmSignature, UserSignature, BillWordingSettings, BillWordingPatch, Bill, BillEvent, CaseBilling, ClientStatement, CreditNoteMode, CreditNotePreview, Disbursement, FirmBills, BillType, CostsFormat, VatTreatment } from '../types/billing.types'
import type { Appearance } from '../types/appearance.types'
import type { MobileChange } from '../types/mobileNumber.types'
import type { EsignContactCheck, EsignSettings, EsignSettingsPatch, EsignSettingsSaved, SealCertificateInfo, SignBody, SignDeclineKind, SignerLinkLookup, SignReadMethod, SignResult, SignSessionRefresh, SigningRequestDetail, SigningRequestSummary, StatementEsign, VerifyByRefResult, VerifyUploadResult } from '../types/esign.types'
export const BASE_URL = process.env.NODE_ENV === 'development' ? '' : 'https://casevo-production.up.railway.app'
// Create axios instance
const api = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
})
// (D12 live updates) This tab's id on every call — the server stamps it on the
// live messages the write causes, so this tab can skip its own echo.
api.defaults.headers.common['X-Casevo-Tab'] = TAB_ID
// Request interceptor — attaches JWT token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('casevo_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})
// ★ Bill writes from THIS tab → services/ownBillWrites, so this tab skips the
// server's "bill-changed" echo of its own save (it already has the fresh bill
// from the response); other users + this user's other tabs still refresh.
// Marked at REQUEST time when the URL names the bill (the echo can race the
// response), and from the response for a write that returns a bill (a new
// draft, a credit-note issue, Send bill).
const BILL_WRITE_URL = /^\/api\/invoices\/([^/?]+)/
const isWrite = (m?: string): boolean => !!m && m.toLowerCase() !== 'get'
api.interceptors.request.use((config) => {
  const id = isWrite(config.method) ? BILL_WRITE_URL.exec(config.url ?? '')?.[1] : undefined
  if (id) markOwnBillWrite(id)
  return config
})
api.interceptors.response.use((response) => {
  if (isWrite(response.config.method)) {
    const d = (response.data as { data?: { id?: unknown; lines?: unknown; bill?: { id?: unknown } | null } } | undefined)?.data
    if (d && typeof d.id === 'string' && Array.isArray(d.lines)) markOwnBillWrite(d.id)
    if (d?.bill && typeof d.bill.id === 'string') markOwnBillWrite(d.bill.id)
  }
  return response
})
// Response interceptor — extracts backend error envelope on HTTP error.
// Without this, an axios 4xx/5xx throws a generic "Request failed with
// status code X" message because the response body is never read by
// our unwrap helpers. The backend always sends { success: false, error }
// alongside the HTTP error code, so we pull that error string out and
// re-throw so callers see e.g. "Current password is incorrect" verbatim.
// Auth endpoints where 401 has a NORMAL-FLOW meaning rather than
// "your session expired." A 401 hitting any of these comes from the
// user being not-yet-authenticated (login/register/reset) or from a
// credential check inside the request body (change-password's
// `currentPassword` field) — none of those should trigger the
// global session-clear. Match by startsWith on error.config.url so
// query strings don't break the comparison.
//
// /api/auth/me is INTENTIONALLY ABSENT from this list — a 401 on
// fetching/updating the current user is the canonical session-
// expired signal and MUST trigger the redirect.
const UNAUTHORIZED_SKIP_REDIRECT_URLS = [
  '/api/auth/login',
  '/api/auth/register',
  '/api/auth/password',
  '/api/auth/request-reset',
  '/api/auth/accept-reset',
  '/api/invites/verify',
  '/api/invites/accept',
] as const

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const envelopeError = error?.response?.data?.error
    if (typeof envelopeError === 'string' && envelopeError.length > 0) {
      // Mutate the AxiosError's .message in place so callers using
      // axios.isAxiosError(err) still get a true AxiosError with
      // err.response, err.response.status, etc. — and any caller that
      // just reads err.message gets the backend's specific message.
      // Previous version returned new Error(envelopeError) which broke
      // every catch block guarded by axios.isAxiosError(err) — they all
      // fell through to their network-failure branch.
      if (error instanceof Error) {
        error.message = envelopeError
      }
    }
    // Session-expired auto-logout. A 401 from any non-skipped
    // endpoint clears the session via clearAuth(), which sets
    // isAuthenticated:false + authView:'login' + resets takeover/
    // content state — App.tsx's next render lands on LoginScreen.
    // Promise.reject below still fires so the caller's .catch can
    // run, but by then the tree is mid-replacement with LoginScreen
    // and the "failed to load" state never paints to the user.
    //
    // Dynamic import — keeps the static graph acyclic (the store
    // imports many runtime helpers from this module at top level;
    // a top-level runtime import here would create a cycle, which
    // the deliberate TYPE-ONLY AuthUser import at the top of this
    // file goes out of its way to avoid). By the time this
    // interceptor fires on a real 401, useCasevoStore is fully
    // loaded — the dynamic import is a cheap lookup, not a
    // re-execution.
    if (error?.response?.status === 401) {
      const url: string = error?.config?.url ?? ''
      const isSkippedAuthEndpoint = UNAUTHORIZED_SKIP_REDIRECT_URLS.some(
        (skip) => url.startsWith(skip),
      )
      // ★ (Audit) A 401 for a request sent with an OLDER token than the one
      // now stored (e.g. in flight while a password change rotated the
      // session) must not sign the user out of the fresh session.
      const sentAuth = String(error?.config?.headers?.Authorization ?? error?.config?.headers?.authorization ?? '')
      const current = localStorage.getItem('casevo_token')
      const staleToken = !!current && sentAuth !== '' && sentAuth !== `Bearer ${current}`
      if (!isSkippedAuthEndpoint && !staleToken) {
        import('../store/useCasevoStore').then((m) => {
          m.useCasevoStore.getState().clearAuth()
        }).catch(() => {
          // Defensive — if the dynamic import somehow fails (it
          // shouldn't), still clear the auth localStorage so the
          // next hard-reload lands on Login.
          localStorage.removeItem('casevo_token')
          localStorage.removeItem('casevo_user')
        })
      }
    }
    // Server-enforced firm 2FA (backend lib/sessionPolicy.ts): a 403 with
    // code TWO_FACTOR_SETUP_REQUIRED means the firm requires 2FA and this user
    // hasn't set it up (e.g. an admin switched it on mid-session). Mark the
    // current user as not-enrolled so App.tsx's forced-setup gate takes over
    // instead of every screen failing. No logout (403, not 401). Debounced so
    // a burst of parallel 403s only flips it once.
    if (error?.response?.status === 403 && error?.response?.data?.code === 'TWO_FACTOR_SETUP_REQUIRED') {
      import('../store/useCasevoStore').then((m) => {
        const st = m.useCasevoStore.getState()
        const u = st.authUser
        if (u && !(u.firmRequire2fa === true && u.twoFactorEnabled !== true)) {
          st.setAuthUser({ ...u, firmRequire2fa: true, twoFactorEnabled: false })
        }
      }).catch(() => { /* non-fatal */ })
    }
    return Promise.reject(error)
  }
)
// ── ENVELOPE UNWRAPPERS ────────────────────────────────────
// All backend responses share the `{ success, data, error }` envelope.
// `unwrap<T>` is for endpoints that return a `data` payload;
// `unwrapVoid` is for DELETE-style endpoints that succeed with no
// `data`. Both throw on `success === false` so callers can use
// plain try/catch instead of branching on `response.success`.
const unwrap = <T,>(payload: { success: boolean; data?: T; error?: string }, what: string): T => {
  if (!payload.success || payload.data === undefined) {
    throw new Error(payload.error || `Failed to ${what}`)
  }
  return payload.data
}
const unwrapVoid = (payload: { success: boolean; error?: string }, what: string): void => {
  if (!payload.success) {
    throw new Error(payload.error || `Failed to ${what}`)
  }
}
// `unwrapPaginated<T>` is for list endpoints with a sibling
// `pagination` object alongside `data` in the envelope (audit
// logs today; future paginated list endpoints will reuse it).
// Returns BOTH the data array and the pagination metadata,
// throwing on `success === false` like the other unwrappers.
const unwrapPaginated = <T,>(
  payload: { success: boolean; data?: T; pagination?: Pagination; error?: string },
  what: string,
): { data: T; pagination: Pagination } => {
  if (!payload.success || payload.data === undefined || payload.pagination === undefined) {
    throw new Error(payload.error || `Failed to ${what}`)
  }
  return { data: payload.data, pagination: payload.pagination }
}
// Shape of the auth endpoints' `data` field. Mirrors what setAuth
// expects in the store: a JWT plus the canonical user record.
interface AuthResponse {
  token: string
  user: AuthUser
}
// ── AUTH API CALLS ─────────────────────────────────────────
// Login can now resolve two ways: a completed login ({ token, user }) or
// a 2FA challenge ({ twoFactorRequired: true, preAuthToken }). A
// discriminated union lets the caller branch cleanly. NOTE we can't use
// unwrap<AuthResponse> here: on the 2FA branch `data` is present but has
// no `token`, so unwrap would hand back a malformed AuthResponse — we
// inspect the envelope ourselves instead.
export type LoginResult =
  | { twoFactorRequired: false; token: string; user: AuthResponse['user'] }
  | { twoFactorRequired: true; preAuthToken: string }
export const loginApi = async (email: string, password: string): Promise<LoginResult> => {
  const response = await api.post('/api/auth/login', { email, password })
  const payload = response.data as {
    success: boolean
    data?: { twoFactorRequired?: boolean; preAuthToken?: string; token?: string; user?: AuthResponse['user'] }
    error?: string
  }
  if (!payload.success || !payload.data) {
    throw new Error(payload.error || 'Failed to log in')
  }
  if (payload.data.twoFactorRequired) {
    return { twoFactorRequired: true, preAuthToken: payload.data.preAuthToken as string }
  }
  return {
    twoFactorRequired: false,
    token: payload.data.token as string,
    user: payload.data.user as AuthResponse['user'],
  }
}
// Complete a 2FA login: exchange the pre-auth token (from a login that
// returned { twoFactorRequired: true, preAuthToken }) plus the user's
// TOTP or backup code for a real session. Returns the SAME { token, user }
// shape as loginApi, so the caller finishes login identically. Stage 2:
// the login-screen wiring that branches on twoFactorRequired is Stage 3.
export const verify2faApi = async (preAuthToken: string, token: string): Promise<AuthResponse> => {
  const response = await api.post('/api/auth/2fa/verify', { preAuthToken, token })
  return unwrap<AuthResponse>(response.data, 'verify 2FA')
}
export const registerApi = async (
  email: string,
  password: string,
  firstName: string,
  lastName: string,
  firmId: string
): Promise<AuthResponse> => {
  const response = await api.post('/api/auth/register', {
    email, password, firstName, lastName, firmId,
  })
  return unwrap<AuthResponse>(response.data, 'register')
}
export const getMeApi = async (): Promise<AuthUser> => {
  const response = await api.get('/api/auth/me')
  return unwrap<AuthUser>(response.data, 'fetch user profile')
}
export const updateMeApi = async (payload: { firstName?: string; lastName?: string; viewingPaneToggles?: Partial<ViewingPaneToggles>; notificationPreferences?: Partial<NotificationPreferences>; avatarColour?: string; avatarStyle?: 'solid' | 'soft' | 'icon'; emailSignature?: string; appearance?: Appearance }): Promise<AuthUser> => {
  const response = await api.patch('/api/auth/me', payload)
  return unwrap<AuthUser>(response.data, 'update user profile')
}
export const changePasswordApi = async (payload: { currentPassword: string; newPassword: string }): Promise<{ message: string; token?: string }> => {
  const response = await api.patch('/api/auth/password', payload)
  return unwrap<{ message: string; token?: string }>(response.data, 'change password')
}
export const requestPasswordResetApi = async (email: string): Promise<void> => {
  const response = await api.post('/api/auth/request-reset', { email })
  // The endpoint always returns { success: true, message: '...' } regardless of
  // whether the email matched a user — account enumeration prevention. We don't
  // surface the message to the user, the frontend shows its own static text.
  if (!response.data?.success) {
    throw new Error(response.data?.error || 'Failed to request password reset')
  }
}
export const acceptPasswordResetApi = async (token: string, newPassword: string): Promise<void> => {
  const response = await api.post('/api/auth/accept-reset', { token, newPassword })
  if (!response.data?.success) {
    throw new Error(response.data?.error || 'Failed to reset password')
  }
}
// ── INVITE API CALLS ───────────────────────────────────────
// Public endpoints reached from the AcceptInviteScreen. Both are in
// UNAUTHORIZED_SKIP_REDIRECT_URLS above so a 401 here means "invalid
// invite token" rather than "your session expired" — without the skip,
// every unauthenticated visitor following an invite link would get
// the global session-clear treatment and bounce to login.
//
// Verify-then-accept: the screen calls verifyInviteApi on mount to
// pre-flight the token (cheap GET) before showing the form, then
// acceptInviteApi on submit to atomically create the user + return
// an auth response (mirrors the login/register shape so the screen
// can call setAuth(auth.token, auth.user) and land the user
// straight in the app).
interface InviteVerifyResponse {
  email: string
  role: string
  firmName: string
  expiresAt: string
}
export const verifyInviteApi = async (token: string): Promise<InviteVerifyResponse> => {
  const response = await api.get(`/api/invites/verify?token=${encodeURIComponent(token)}`)
  if (!response.data?.success) {
    throw new Error(response.data?.error || 'This invite link is invalid or has expired.')
  }
  return response.data.data as InviteVerifyResponse
}
export const acceptInviteApi = async (payload: {
  token: string
  firstName: string
  lastName: string
  password: string
}): Promise<AuthResponse> => {
  const response = await api.post('/api/invites/accept', payload)
  if (!response.data?.success) {
    throw new Error(response.data?.error || 'Failed to accept invite.')
  }
  return response.data.data as AuthResponse
}
// ── CASE API CALLS ─────────────────────────────────────────
export const fetchCases = async (options?: {
  recent?: boolean
  // Stage A + C: personal-filter param wired through to the backend's
  // `?assignedToUserId=me|<uuid>` handler. Omitted → unfiltered
  // (default). 'me' is the sentinel the backend resolves to the
  // caller's userId (mirrors taskController.getTasks). Non-admins may
  // only pass 'me' or their own userId — a raw cross-user uuid from a
  // non-admin surfaces as a 403 (caller must catch).
  assignedToUserId?: 'me' | string
  // Stage C: backend already supports `?status=open` (only cases with
  // closeDate null/empty). Wired through so the Dashboard's "my open
  // cases" query is a single filtered call instead of client-side
  // bucketing over the full list.
  status?: 'open'
  // Callum #3 "recent by day": an explicit LOCAL-day window. When BOTH
  // are given, the backend returns cases THIS user viewed with
  // CaseAccess.lastViewedAt inside [from, to] (per-user, sorted by
  // lastViewedAt desc — the same shape as ?recent=24h, just a specific
  // day). Both are ISO instants; the CALLER computes local-midnight →
  // 23:59:59.999 so days are bounded by the user's local day, not UTC.
  from?: string
  to?: string
}): Promise<RawCase[]> => {
  // P5 backend filter: ?recent=24h returns only cases the user has
  // viewed in the last 24 hours (by lastAccessedAt desc). Omitted
  // when options.recent is falsy so the default unfiltered list
  // path stays a clean GET /api/cases with no query string.
  const params: Record<string, string> = {}
  if (options?.recent) params.recent = '24h'
  if (options?.assignedToUserId) params.assignedToUserId = options.assignedToUserId
  if (options?.status) params.status = options.status
  // Day-range window (both required to take effect — a lone bound is
  // ignored, matching the backend's `from && to` guard).
  if (options?.from && options?.to) {
    params.from = options.from
    params.to = options.to
  }
  const response = await api.get('/api/cases', {
    params: Object.keys(params).length ? params : undefined,
  })
  return unwrap<RawCase[]>(response.data, 'load cases')
}
// Paginated variant. The backend's getCases opt-in pagination activates
// when ?page or ?limit is present and returns the audit-log envelope
// shape ({ data, pagination }) — we unwrap via the shared helper.
// Existing callers of fetchCases() above are unaffected; this is a
// new entry point for the case-list browse mode (Option A pagination
// in CaseListScreen: paginate when not searching/filtering).
// ★ (Audit H6) Every filter the case list offers runs on the server, paged:
//   q          — the search box (backend lib/caseListSearch)
//   statusKey  — Draft / Open / Closed (the list's Status)
//   limitation — 'missing' → open cases with no limitation date
//   assignedToUserId — 'me' | <uuid> | 'unassigned'
//   claimType  — a ClaimType or 'none' (not set) — utils/claimTypeFilter
// (Big-audit batch 4) A search box's text goes in a header, never the URL (URLs
// are written to the hosting logs; people search by name and NI number).
const searchHeaders = (term: string | undefined): Record<string, string> | undefined =>
  (term && term.trim() ? { 'X-Casevo-Search': encodeURIComponent(term.trim()) } : undefined)

export const fetchCasesPaginated = async (
  page: number,
  limit: number,
  options?: { recent?: boolean; assignedToUserId?: 'me' | string; q?: string; statusKey?: 'draft' | 'open' | 'closed'; limitation?: 'missing'; claimType?: ClaimTypeFilterValue },
): Promise<{ data: RawCase[]; pagination: Pagination }> => {
  const params: Record<string, string> = { page: String(page), limit: String(limit) }
  if (options?.recent) params.recent = '24h'
  if (options?.statusKey) params.statusKey = options.statusKey
  if (options?.limitation) params.limitation = options.limitation
  if (options?.claimType) params.claimType = options.claimType
  // Case-list assignee filter: threads through the same
  // ?assignedToUserId=me|<uuid> the unpaginated fetchCases uses
  // (see options-shape docblock at L237-249). Backend resolves 'me'
  // to req.user.userId (caseController.getCases L62). Non-admins
  // passing a cross-user uuid surface as 403 — caller must catch.
  if (options?.assignedToUserId) params.assignedToUserId = options.assignedToUserId
  const response = await api.get('/api/cases', { params, headers: searchHeaders(options?.q) })
  return unwrapPaginated<RawCase[]>(response.data, 'load cases')
}
// The case list's claim type filter options — GET /api/cases/claim-types with
// the list's OTHER filters (the same params as fetchCasesPaginated, minus the
// claim type and paging): only the types present, with counts, + not set.
export const fetchCaseClaimTypeOptionsApi = async (
  options?: { recent?: boolean; assignedToUserId?: 'me' | string; q?: string; statusKey?: 'draft' | 'open' | 'closed'; limitation?: 'missing' },
): Promise<ClaimTypeOptions> => {
  const params: Record<string, string> = {}
  if (options?.recent) params.recent = '24h'
  if (options?.statusKey) params.statusKey = options.statusKey
  if (options?.limitation) params.limitation = options.limitation
  if (options?.assignedToUserId) params.assignedToUserId = options.assignedToUserId
  const response = await api.get('/api/cases/claim-types', { params, headers: searchHeaders(options?.q) })
  return unwrap<ClaimTypeOptions>(response.data, 'load claim types')
}
// ★ LOCKED CASE LINKS — which of these case ids may the current user open?
// POST /api/cases/access-check (backend lib/caseAccess.ts accessibleCaseIds —
// the same rule as every case guard). Returns the accessible subset.
export const checkCaseAccessApi = async (ids: string[]): Promise<string[]> => {
  const response = await api.post('/api/cases/access-check', { ids })
  return unwrap<{ accessible: string[] }>(response.data, 'check case access').accessible
}

// ★ CASE ACCESS — user-facing message for a case the server refused to open.
// 403 = the case exists but this user isn't in its audience (backend
// lib/caseAccess.ts); 404 = it doesn't exist (e.g. deleted). null = any other
// error (caller keeps its own handling).
export const caseOpenErrorMessage = (err: unknown): string | null => {
  const status = axios.isAxiosError(err) ? err.response?.status : undefined
  if (status === 403) return "You don't have access to this case."
  if (status === 404) return 'This case no longer exists.'
  return null
}
// (Audit batch 3, H4) The client is behind an ethical wall: the server
// answered 403 { restricted: true } (backend lib/clientAccess) — the viewer
// can't reach any of the client's cases. The UI shows "Restricted client".
export const isRestrictedClientError = (err: unknown): boolean =>
  axios.isAxiosError(err) && err.response?.status === 403 && (err.response?.data as { restricted?: boolean } | undefined)?.restricted === true
export const RESTRICTED_CLIENT_TIP = 'You don’t have access to any of this client’s cases, so their details are hidden.'
export const fetchCaseById = async (id: string): Promise<RawCase> => {
  const response = await api.get(`/api/cases/${id}`)
  return unwrap<RawCase>(response.data, 'load case')
}
// ★ CASE-OPEN de-dupe. The quick-view modal fetches the full case (+ its
// deadlines) the moment it opens; clicking "Full case →" before that request
// returns used to fire a SECOND, cold getCaseById (and deadlines) for the same
// case. These shared variants join an IN-FLIGHT request for the same id instead
// of starting another; once it settles the entry is dropped, so a later call is
// always a fresh read. Used ONLY by the case-open paths (quick-view +
// setCurrentCaseById) — refresh / 409-recovery callers keep fetchCaseById.
const inflightCase = new Map<string, Promise<RawCase>>()
export const fetchCaseByIdShared = (id: string): Promise<RawCase> => {
  const hit = inflightCase.get(id)
  if (hit) return hit
  const p = fetchCaseById(id).finally(() => { inflightCase.delete(id) })
  inflightCase.set(id, p)
  return p
}
// Backend (Task 1.4) requires { clientId } in the POST body so the
// new Case row can be linked to its Client. Callers MUST resolve a
// clientId first — typically via ClientPickerModal — before
// invoking this helper.
// ★ (B2) …and a claim type (400 { field: 'claimType' } without one); the
// route is optional (components/cases/ClaimTypeFields pre-fills a suggestion).
export const createCaseApi = async (payload: { clientId: string; claimType: ClaimType; claimRoute?: ClaimRoute | null }): Promise<RawCase> => {
  const response = await api.post('/api/cases', payload)
  return unwrap<RawCase>(response.data, 'create case')
}
// ── CSV import — ALL OR NOTHING (POST /api/cases/import) ──
// One request for every importable row; the server creates them inside one
// transaction. A failed row rolls everything back → ImportFailedError naming
// that row (row = the CSV line) and why.
export interface ImportCaseRow {
  row: number
  client: { linkClientId: string } | { create: Record<string, unknown>; key?: string }
  // (B2) Required — the import asks once for the whole file.
  claimType: ClaimType
  claimRoute?: ClaimRoute | null
  caseUpdates?: Record<string, unknown>
  leaveUnassigned?: boolean
}
export class ImportFailedError extends Error {
  constructor(public row: number | null, public reason: string) { super(reason) }
}
export const importCasesApi = async (rows: ImportCaseRow[]): Promise<{ imported: number; caseIds: string[] }> => {
  try {
    const response = await api.post('/api/cases/import', { rows }, { timeout: 180_000 })
    return unwrap<{ imported: number; caseIds: string[] }>(response.data, 'import cases')
  } catch (err) {
    const data = axios.isAxiosError(err) ? err.response?.data as { failedRow?: number; reason?: string; error?: string } | undefined : undefined
    if (data && typeof data.failedRow === 'number') throw new ImportFailedError(data.failedRow, data.reason ?? data.error ?? 'Failed')
    throw new ImportFailedError(null, data?.error ?? 'The import failed and nothing was imported. Please try again.')
  }
}
export const updateCaseApi = async (id: string, updates: Record<string, unknown>): Promise<RawCase> => {
  const response = await api.put(`/api/cases/${id}`, updates)
  return unwrap<RawCase>(response.data, 'update case')
}
// (B2b) Child sexual abuse "no time limit" (Limitation Act s.11ZA): confirm
// (all three statements ticked) / remove the confirmation. The server checks
// it applies (sexual abuse, a child at the accident date) and audit-logs both.
// → the case row + abuseNoLimitConfirmedBy.
export const confirmAbuseNoLimitApi = async (id: string, statements: { sexualAbuse: boolean; underEighteen: boolean; neverSettled: boolean }): Promise<RawCase> => {
  const response = await api.post(`/api/cases/${id}/abuse-no-limit`, { statements })
  return unwrap<RawCase>(response.data, "confirm no time limit")
}
export const unconfirmAbuseNoLimitApi = async (id: string): Promise<RawCase> => {
  const response = await api.delete(`/api/cases/${id}/abuse-no-limit`)
  return unwrap<RawCase>(response.data, "remove the no time limit confirmation")
}
// POST /api/cases/:id/sync-client — re-snapshots all 29 client snapshot
// fields (15 person + 14 GP/Hospital) from the live Client onto this Case
// row. Triggered by the "Update from current client details" button on
// the case-detail Client Details section (Client Snapshot Model, Stage 2c).
// Backend fires the case.client_synced audit event on success. Returns
// the updated case in the same RawCase shape as fetchCaseById, with the
// linked Client embedded.
// ★ (Audit H3) Body = what the user CONFIRMED: the record's value for each
// listed field (only those are copied) + the record's updatedAt. If the record
// changed since, the server answers 409 (code CLIENT_RECORD_CHANGED) and
// writes nothing — see isClientRecordChanged.
export const syncClientApi = async (
  id: string,
  confirmed: { fields: Record<string, unknown>; clientUpdatedAt?: string },
): Promise<RawCase> => {
  const response = await api.post(`/api/cases/${id}/sync-client`, confirmed)
  return unwrap<RawCase>(response.data, 'sync client snapshot')
}
// (Client-sync) ONE request for the contact note's two actions on one group:
// 'toRecord' = this case's values onto the client record ("Update the client
// record"); 'toCase' = the record's values onto this case ("Use the client
// record's …"). `shown` = the values the user was looking at; the server
// checks them against the current case + record in one transaction and
// returns both sides as they now are (+ their stamps) — no re-read needed.
// A 409 (isClientRecordChanged) carries both current values in data.
export interface ContactSyncResult {
  group: 'email' | 'phone' | 'address'
  direction: 'toRecord' | 'toCase'
  changed: string[]
  case: Record<string, unknown>
  record: Record<string, unknown>
  caseUpdatedAt: string | null
  clientUpdatedAt: string | null
}
export const contactSyncApi = async (
  id: string,
  body: { group: 'email' | 'phone' | 'address'; direction: 'toRecord' | 'toCase'; shown: { case: Record<string, unknown>; record: Record<string, unknown> } },
): Promise<ContactSyncResult> =>
  unwrap<ContactSyncResult>((await api.post(`/api/cases/${id}/contact-sync`, body)).data, 'update the contact details')
// The sync was refused because the client record changed after the confirm.
export const isClientRecordChanged = (err: unknown): boolean => {
  const r = (err as { response?: { status?: number; data?: { code?: string } } } | null)?.response
  return r?.status === 409 && r.data?.code === 'CLIENT_RECORD_CHANGED'
}
// Stage A: admin-only reassignment. PATCH /api/cases/:id/assignee
// with { assignedToUserId }. String → new assignee; null → clear.
// Backend 403s for non-admins (route-level requireAdmin) and 404s for
// cross-firm case ids or missing/inactive target users. Returns the
// updated RawCase. Kept OFF the routine updateCaseApi path on purpose
// — reassignment is a deliberate admin action, not an autosave field.
export const updateCaseAssigneeApi = async (
  caseId: string,
  assignedToUserId: string | null,
): Promise<RawCase> => {
  const response = await api.patch(`/api/cases/${caseId}/assignee`, { assignedToUserId })
  return unwrap<RawCase>(response.data, 'update case assignee')
}
export const deleteCaseApi = async (id: string): Promise<void> => {
  const response = await api.delete(`/api/cases/${id}`)
  unwrapVoid(response.data, 'delete case')
}
// ── CASE LINKING ───────────────────────────────────────────
// Firm-wide case-to-case links (CaseLink join table). Both endpoints are
// JWT-protected and NOT admin-gated — any firm member may link/unlink.
// POST /api/cases/:id/links  { targetCaseId, linkType? } → the linked
// case's summary. Backend validates same-firm (404), rejects self-link
// (400), and dup-guards both directions (409). linkType is optional
// (null = a plain "linked" relationship).
// Round 2: `relationship` (SAME_ACCIDENT / RELATED / OTHER) is chosen in the
// link dialog; SAME_ACCIDENT also joins the cases' accident group.
export const linkCaseApi = async (
  caseId: string,
  targetCaseId: string,
  relationship?: CaseLinkRelationship,
): Promise<CaseLinkSummary> => {
  const response = await api.post(`/api/cases/${caseId}/links`, { targetCaseId, linkType: null, relationship: relationship ?? null })
  return unwrap<CaseLinkSummary>(response.data, 'link case')
}
// PATCH /api/cases/:id/links/:targetCaseId { relationship } — set / confirm
// how two linked cases are connected (clears "please confirm").
export const updateCaseLinkRelationshipApi = async (caseId: string, targetCaseId: string, relationship: CaseLinkRelationship): Promise<void> => {
  const response = await api.patch(`/api/cases/${caseId}/links/${targetCaseId}`, { relationship })
  unwrapVoid(response.data, 'update link')
}
// DELETE /api/cases/:id/links/:targetCaseId — removes the link in either
// direction (a link is mutual). 404 if no such link.
export const unlinkCaseApi = async (caseId: string, targetCaseId: string): Promise<void> => {
  const response = await api.delete(`/api/cases/${caseId}/links/${targetCaseId}`)
  unwrapVoid(response.data, 'unlink case')
}
// ── (Batch 4) ACCIDENT GROUPING ────────────────────────────
// Heuristic suggestions (never auto-linked) + the CONFIRMED group. Members the
// viewer can't open come back as { id, restricted: true } with nothing else.
export interface AccidentCaseRef {
  id: string
  restricted?: boolean
  name?: string
  ref?: string
  clientName?: string
  accidentDate?: string | null
}
export interface AccidentSuggestion extends AccidentCaseRef {
  score: number
  reasons: string[]
  inAnotherGroup: boolean
}
export interface AccidentGroupResult { groupId: string | null; cases: AccidentCaseRef[] }
export const getAccidentGroupApi = async (caseId: string): Promise<AccidentGroupResult> => {
  const response = await api.get(`/api/cases/${caseId}/accident-group`)
  return unwrap<AccidentGroupResult>(response.data, 'load accident group')
}
export const getAccidentSuggestionsApi = async (caseId: string): Promise<AccidentSuggestion[]> => {
  const response = await api.get(`/api/cases/${caseId}/accident-suggestions`)
  return unwrap<AccidentSuggestion[]>(response.data, 'load accident suggestions')
}
export const linkAccidentGroupApi = async (caseId: string, otherCaseId: string): Promise<AccidentGroupResult> => {
  const response = await api.post(`/api/cases/${caseId}/accident-group`, { otherCaseId })
  return unwrap<AccidentGroupResult>(response.data, 'link same accident')
}
export const unlinkAccidentGroupApi = async (caseId: string): Promise<void> => {
  const response = await api.delete(`/api/cases/${caseId}/accident-group`)
  unwrapVoid(response.data, 'unlink accident group')
}
export const dismissAccidentSuggestionApi = async (caseId: string, otherCaseId: string): Promise<void> => {
  const response = await api.post(`/api/cases/${caseId}/accident-suggestions/dismiss`, { otherCaseId })
  unwrapVoid(response.data, 'dismiss suggestion')
}
// ── WITNESS API CALLS ──────────────────────────────────────
// All endpoints are JWT-protected and respond with the standard
// `{ success, data, error }` envelope.
export const listWitnessesApi = async (caseId: string): Promise<Witness[]> => {
  const response = await api.get(`/api/cases/${caseId}/witnesses`)
  return unwrap<Witness[]>(response.data, 'load witnesses')
}
export const createWitnessApi = async (caseId: string): Promise<Witness> => {
  // POST with an empty body — the backend fills in blank defaults and
  // returns the new row (including its server-generated id).
  const response = await api.post(`/api/cases/${caseId}/witnesses`, {})
  return unwrap<Witness>(response.data, 'create witness')
}
export const updateWitnessApi = async (
  witnessId: string,
  // baseUpdatedAt (optional) is the optimistic-concurrency token — read by the
  // backend guard, never written as a column.
  changes: Partial<Witness> & { baseUpdatedAt?: string },
): Promise<Witness> => {
  const response = await api.patch(`/api/cases/witnesses/${witnessId}`, changes)
  return unwrap<Witness>(response.data, 'update witness')
}
export const deleteWitnessApi = async (witnessId: string): Promise<void> => {
  const response = await api.delete(`/api/cases/witnesses/${witnessId}`)
  unwrapVoid(response.data, 'delete witness')
}
// ── TPI CONTACT (FIRM-WIDE DIRECTORY) ──────────────────────
// Same `{ success, data, error }` envelope, same unwrap-or-throw.
export type TpiContactInput = Omit<TpiContact, 'id' | 'firmId' | 'createdAt' | 'updatedAt'>
export const listTpiContactsApi = async (): Promise<TpiContact[]> => {
  const response = await api.get('/api/tpi-contacts')
  return unwrap<TpiContact[]>(response.data, 'load TPI contacts')
}
export const createTpiContactApi = async (data: TpiContactInput): Promise<TpiContact> => {
  const response = await api.post('/api/tpi-contacts', data)
  return unwrap<TpiContact>(response.data, 'save TPI contact')
}
export const updateTpiContactApi = async (
  id: string,
  changes: Partial<TpiContact>,
): Promise<TpiContact> => {
  const response = await api.patch(`/api/tpi-contacts/${id}`, changes)
  return unwrap<TpiContact>(response.data, 'update TPI contact')
}
// ── CLIENT API CALLS ───────────────────────────────────────
// Same unwrap-or-throw pattern as the others.

export const listClientsApi = async (search?: string, sort?: string): Promise<Client[]> => {
  // Build params lazily so we only send a query string when the
  // caller actually asked for filtering or sorting. axios serialises
  // an empty {} to "?" which is harmless but ugly — undefined skips
  // the query string entirely.
  const params: Record<string, string> = {}
  if (sort) params.sort = sort
  const response = await api.get('/api/clients', { params: Object.keys(params).length ? params : undefined, headers: searchHeaders(search) })
  return unwrap<Client[]>(response.data, 'load clients')
}

export const getClientByIdApi = async (id: string): Promise<Client> => {
  const response = await api.get(`/api/clients/${id}`)
  return unwrap<Client>(response.data, 'load client')
}

export const createClientApi = async (payload: CreateClientPayload): Promise<Client> => {
  const response = await api.post('/api/clients', payload)
  return unwrap<Client>(response.data, 'create client')
}

export const updateClientApi = async (
  id: string,
  // baseUpdatedAt (optional) is the optimistic-concurrency token — the
  // server updatedAt the client was loaded with. It rides alongside the
  // whitelisted fields; the backend reads it for the 409 guard and never
  // writes it as a column.
  payload: UpdateClientPayload & { baseUpdatedAt?: string },
): Promise<Client> => {
  const response = await api.patch(`/api/clients/${id}`, payload)
  return unwrap<Client>(response.data, 'update client')
}

// Delete a Client. Backend returns 409 when the client still has
// linked cases (onDelete: Restrict on the Client→Case relation);
// we surface that message verbatim so the picker can render the
// user-actionable text ("Cannot delete client with active cases…")
// instead of axios's generic "Request failed with status code 409".
export const deleteClientApi = async (id: string): Promise<void> => {
  try {
    const response = await api.delete(`/api/clients/${id}`)
    unwrapVoid(response.data, 'delete client')
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.data?.error) {
      throw new Error(err.response.data.error)
    }
    throw err instanceof Error ? err : new Error('Failed to delete client')
  }
}

export const getCasesForClientApi = async (clientId: string): Promise<unknown[]> => {
  const response = await api.get(`/api/clients/${clientId}/cases`)
  return unwrap<unknown[]>(response.data, 'load cases for client')
}

// ── USER API CALLS ─────────────────────────────────────────
// Returns all users in the caller's firm. No role gating — needed
// for assignee dropdowns where any worker can read the list.
export const listUsersApi = async (): Promise<FirmUser[]> => {
  const response = await api.get('/api/users')
  return unwrap<FirmUser[]>(response.data, 'load users')
}
// PATCH a user's role (admin-only on the backend). Returns the
// updated FirmUser PLUS a `note` string the backend includes to
// instruct the affected user to log out + back in for the change
// to take effect. unwrap<T> handles the standard {success,data}
// envelope (throws backend Error.message on !success); we read
// `note` separately off the raw envelope.
//
// Backend rejects:
//   - 403 if caller isn't admin
//   - 400 'You cannot remove your own admin access' (self-demotion)
//   - 400 'Cannot remove the last admin from a firm'
//   - 400 'Invalid role'
//   - 404 if userId is unknown or cross-firm
// All surface as Error(message) for the caller's catch block.
export const updateUserRoleApi = async (userId: string, role: 'admin' | 'user'): Promise<{ user: FirmUser; note: string }> => {
  const response = await api.patch(`/api/users/${userId}/role`, { role })
  const user = unwrap<FirmUser>(response.data, 'update user role')
  const note = typeof response.data?.note === 'string' ? response.data.note : ''
  return { user, note }
}
export const deactivateUserApi = async (userId: string): Promise<FirmUser> => {
  const response = await api.patch(`/api/users/${userId}/deactivate`)
  return unwrap<FirmUser>(response.data, 'deactivate user')
}
export const reactivateUserApi = async (userId: string): Promise<FirmUser> => {
  const response = await api.patch(`/api/users/${userId}/reactivate`)
  return unwrap<FirmUser>(response.data, 'reactivate user')
}
// PATCH /api/users/:id/grade — admin-only. Sets the user's billing
// grade (A/B/C/D). Unlike updateUserRoleApi, NO 'note' sibling — the
// backend reads billingGrade live from the User row (not the JWT), so
// there's no log-out-to-take-effect caveat. Returns the updated
// FirmUser. Errors surface as Error(message) via unwrap.
export const updateUserBillingGradeApi = async (
  userId: string,
  billingGrade: 'A' | 'B' | 'C' | 'D',
): Promise<FirmUser> => {
  const response = await api.patch(`/api/users/${userId}/grade`, { billingGrade })
  return unwrap<FirmUser>(response.data, 'update user billing grade')
}

// ── INVITES API ────────────────────────────────────────────
// Admin-only team invite management. Surfaced by AdminPage's
// InvitesPanel — list / create / resend / revoke. All four go through
// the normal authenticated `api` client (NOT the public skip-list at
// the top of this file): a 401 here means the admin's session expired,
// not the invite link being invalid. The public AcceptInviteScreen
// uses verifyInviteApi / acceptInviteApi instead (those ARE on the
// skip-list).
//
// All four return the canonical Invite shape; no token is ever
// returned to the admin UI (the token is delivered to the recipient
// via email only — see invite.types.ts docblock).
export const listInvitesApi = async (status?: string): Promise<Invite[]> => {
  // Optional ?status= query filters to a single InviteStatus when set.
  // Omitted entirely when status is falsy so the default unfiltered
  // list path stays a clean GET /api/invites with no query string.
  const url = status && status.length > 0
    ? `/api/invites?status=${encodeURIComponent(status)}`
    : '/api/invites'
  const response = await api.get(url)
  return unwrap<Invite[]>(response.data, 'load invites')
}
export const createInviteApi = async (payload: { email: string; role: 'user' | 'admin' }): Promise<Invite> => {
  const response = await api.post('/api/invites', payload)
  return unwrap<Invite>(response.data, 'send invite')
}
export const resendInviteApi = async (id: string): Promise<Invite> => {
  const response = await api.post(`/api/invites/${id}/resend`)
  return unwrap<Invite>(response.data, 'resend invite')
}
export const revokeInviteApi = async (id: string): Promise<Invite> => {
  const response = await api.post(`/api/invites/${id}/revoke`)
  return unwrap<Invite>(response.data, 'revoke invite')
}

// ── FILE API CALLS (R2-backed) ─────────────────────────────
// Upload is a two-step flow:
//   1. requestUploadUrlApi → backend provisions a fileId + r2Key
//      and returns a presigned uploadUrl
//   2. Frontend PUTs the file bytes DIRECTLY to that uploadUrl
//      (NOT through this api client — straight to R2)
// listFilesApi returns the saved attachments for a case, each with
// the uploader's display fields embedded. requestDownloadUrlApi
// returns a presigned URL the browser can navigate / fetch from
// directly. deleteFileApi removes the row + the R2 object.
export interface FileAttachment {
  id: string
  caseId: string
  firmId: string
  filename: string
  mimeType: string
  sizeBytes: number
  /** No longer returned by the list endpoint (internal storage key). */
  r2Key?: string
  uploadedAt: string
  uploadedBy: string
  uploader: { firstName: string; lastName: string; email: string }
  // Upload lifecycle (schema batch, backend lib/fileStatus.ts): 'pending'
  // from presign until the PUT is confirmed (POST /api/files/:id/complete or
  // the list self-heal), then 'ready'. List endpoints only return ready rows,
  // so a listed file is normally 'ready'; optional for an older backend.
  status?: 'pending' | 'ready'
  // A sent bill's stored PDF — the delivery record the client received. The
  // server refuses to delete it (409 DELIVERED_BILL_PDF), so no Delete is
  // offered. Optional for an older backend.
  deliveredBillPdf?: boolean
  // (E-sign fixes B) A copy of a sealed e-signed PDF / certificate: read-only
  // (no delete / rename / move — the server refuses with 409).
  esigned?: EsignedFileInfo | null
  // (Audit batch 3) "Used as proof for a client instruction" → can't be deleted.
  proofFor?: string | null
  // (D12) An e-signed copy: uploaded by "Casevo e-signature" (uploader.firstName), not a person.
  uploadedBySystem?: boolean
}
export const requestUploadUrlApi = async (
  caseId: string,
  body: { filename: string; mimeType: string; sizeBytes: number },
): Promise<{ fileId: string; uploadUrl: string; r2Key: string; mimeType?: string }> => {
  const response = await api.post(`/api/cases/${caseId}/files/upload-url`, body)
  return unwrap<{ fileId: string; uploadUrl: string; r2Key: string; mimeType?: string }>(response.data, 'request upload URL')
}
export const listFilesApi = async (caseId: string): Promise<FileAttachment[]> => {
  const response = await api.get(`/api/cases/${caseId}/files`)
  return unwrap<FileAttachment[]>(response.data, 'load files')
}
export const requestDownloadUrlApi = async (
  caseId: string,
  fileId: string,
  inline: boolean = false,
): Promise<{ downloadUrl: string; filename: string; mimeType: string; sizeBytes: number }> => {
  // inline=true asks the server to sign an inline (in-tab) URL; the
  // server only honours it for allowlisted mimes, else it signs
  // attachment. Default false → download, byte-identical to before.
  const response = await api.get(`/api/cases/${caseId}/files/${fileId}/download-url`, {
    params: inline ? { disposition: 'inline' } : {},
  })
  return unwrap<{ downloadUrl: string; filename: string; mimeType: string; sizeBytes: number }>(response.data, 'request download URL')
}
export const deleteFileApi = async (caseId: string, fileId: string): Promise<void> => {
  const response = await api.delete(`/api/cases/${caseId}/files/${fileId}`)
  unwrapVoid(response.data, 'delete file')
}
// New firm-scoped flat upload route — accepts EITHER caseId OR clientId
// (XOR validated server-side). Use this for client-level uploads; use
// the case-scoped requestUploadUrlApi above for case-attached files.
export const requestUploadUrlFlatApi = async (params: {
  caseId?: string
  clientId?: string
  filename: string
  mimeType: string
  sizeBytes: number
}): Promise<{ fileId: string; uploadUrl: string; r2Key: string; mimeType?: string }> => {
  const response = await api.post('/api/files/upload-url', params)
  return unwrap<{ fileId: string; uploadUrl: string; r2Key: string; mimeType?: string }>(response.data, 'request upload URL')
}

// Confirm a direct-to-R2 upload landed (pending → ready). Uploader-only.
// Best-effort: list endpoints + note attach also self-heal recent pending files.
export const completeFileUploadApi = async (fileId: string): Promise<void> => {
  const response = await api.post(`/api/files/${fileId}/complete`)
  unwrapVoid(response.data, 'confirm upload')
}
// Flat firm-scoped variant — downloads a file by id, no caseId in URL.
// Works for both case-attached and client-attached files.
export const requestDownloadUrlFlatApi = async (
  fileId: string,
  inline: boolean = false,
): Promise<{ downloadUrl: string; filename: string; mimeType: string; sizeBytes: number }> => {
  // inline=true asks for an in-tab URL; the server only signs inline
  // for allowlisted mimes (non-safe → attachment). Default false →
  // download, byte-identical to the previous single-arg callers.
  const response = await api.get(`/api/files/${fileId}/download-url`, {
    params: inline ? { disposition: 'inline' } : {},
  })
  return unwrap<{ downloadUrl: string; filename: string; mimeType: string; sizeBytes: number }>(
    response.data,
    'request download URL'
  )
}

// Flat firm-scoped variant — deletes a file by id, no caseId in URL.
// Backend handles orphan rows (R2 NoSuchKey) gracefully — DB row is
// always cleaned up regardless of R2 object state.
export const deleteFileFlatApi = async (fileId: string): Promise<void> => {
  const response = await api.delete(`/api/files/${fileId}`)
  unwrapVoid(response.data, 'delete file')
}

// ── SECURE SHARE LINK (Stage 1) ───────────────────────────
// Create an expiring, revocable, audited download LINK for a case/client file —
// used to email a large file as a link instead of an attachment. The backend
// returns the URL (containing the raw token) exactly ONCE; it is never stored
// in plaintext. expiresInDays is whitelisted server-side to 7/14/30 (default 14).
export interface ShareLinkResult {
  id: string // the link's id (to list / cancel it — never the token)
  url: string
  expiresAt: string // ISO 8601
}
// A file's share links (GET /api/files/:fileId/share-links) — never the token.
export interface ShareLinkInfo {
  id: string
  createdBy: string
  createdByName: string | null
  createdAt: string
  expiresAt: string
  revokedAt: string | null
  accessCount: number
  lastAccessedAt: string | null
  status: 'active' | 'expired' | 'revoked'
  canRevoke: boolean // the caller made it, or is an admin
}
export const listShareLinksApi = async (fileId: string): Promise<ShareLinkInfo[]> => {
  const response = await api.get(`/api/files/${fileId}/share-links`)
  return unwrap<ShareLinkInfo[]>(response.data, 'load share links')
}
// Cancel a link now (creator or admin). Already cancelled → fine.
export const revokeShareLinkApi = async (fileId: string, linkId: string): Promise<void> => {
  const response = await api.post(`/api/files/${fileId}/share-links/${linkId}/revoke`)
  unwrap<unknown>(response.data, 'cancel share link')
}
export const createShareLinkApi = async (
  fileId: string,
  expiresInDays: 7 | 14 | 30 = 14,
): Promise<ShareLinkResult> => {
  const response = await api.post(`/api/files/${fileId}/share`, { expiresInDays })
  return unwrap<ShareLinkResult>(response.data, 'create share link')
}

// ── CALENDAR API CALLS ─────────────────────────────────────
// Aggregated feed of task deadlines + case-fact deadlines from the
// firm. Phase 1 (sidebar Phase 2c) calls this with no params; Stage C
// adds optional ISO ?from / ?to range filtering (backend already
// supports these — see calendarController.getCalendar L58-68).
// Personal filtering (assigned-to-me) is NOT a backend param today;
// callers do that client-side by cross-referencing entries against
// their own task list + assignee case list. See DashboardView's
// useDashboardData for the personal-filter composition.
export const fetchCalendarApi = async (options?: {
  from?: string   // ISO 8601, inclusive
  to?: string     // ISO 8601, inclusive
  scope?: 'mine' | 'firm'  // 'mine' = my tasks + my-cases' deadlines; 'firm' = role-scoped tasks + firm deadlines (default firm server-side)
}): Promise<CalendarEntry[]> => {
  const params: Record<string, string> = {}
  if (options?.from) params.from = options.from
  if (options?.to) params.to = options.to
  if (options?.scope) params.scope = options.scope
  const response = await api.get('/api/calendar', {
    params: Object.keys(params).length ? params : undefined,
  })
  return unwrap<CalendarEntry[]>(response.data, 'load calendar')
}
// ── DAY-NOTES API ─────────────────────────────────────────
// Calendar day-notes for a date window (GET /api/day-notes?from&to).
// Returns firm-wide notes PLUS the caller's own private notes in
// range — visibility filtering is enforced server-side. Powers the
// month-grid 🗒 pins. Lazy params: an empty window sends no query
// string (backend then returns an unbounded set, but the grid always
// passes a range). CRUD helpers (create/update/delete) land in Stage 3.
export const fetchDayNotesApi = async (options?: {
  from?: string   // ISO 8601, inclusive
  to?: string     // ISO 8601, inclusive
}): Promise<DayNote[]> => {
  const params: Record<string, string> = {}
  if (options?.from) params.from = options.from
  if (options?.to) params.to = options.to
  const response = await api.get('/api/day-notes', {
    params: Object.keys(params).length ? params : undefined,
  })
  return unwrap<DayNote[]>(response.data, 'load day notes')
}

// ── AI DAY SUMMARY ────────────────────────────────────────
// The browser only talks to OUR backend; the backend calls the AI provider
// server-side (the API key never reaches the frontend). The summary is
// always for the logged-in user's own day. `source` tells the UI whether
// it's a fresh/cached AI summary, a plain fallback list (AI unavailable),
// or an empty day.
export type DaySummarySource = 'ai' | 'cache' | 'fallback' | 'empty' | 'off'
export interface DaySummary {
  date: string
  text: string
  generatedAt: string
  source: DaySummarySource
}
// Local-day bounds + timezone for a 'YYYY-MM-DD' key, so the backend
// windows the day exactly (BST/GMT-correct) and formats times locally.
const daySummaryParams = (date: string): Record<string, string> => {
  const [y, m, d] = date.split('-').map(Number)
  const from = new Date(y, m - 1, d, 0, 0, 0, 0)
  const to = new Date(y, m - 1, d, 23, 59, 59, 999)
  let tz = 'Europe/London'
  try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || tz } catch { /* keep default */ }
  return { date, from: from.toISOString(), to: to.toISOString(), tz }
}
export const fetchDaySummaryApi = async (date: string): Promise<DaySummary> => {
  const response = await api.get('/api/day-summary', { params: daySummaryParams(date) })
  return unwrap<DaySummary>(response.data, 'load day summary')
}
export const refreshDaySummaryApi = async (date: string): Promise<DaySummary> => {
  // Body `{}`, NOT null: with the instance's JSON Content-Type axios serialises
  // null to the literal "null", which express.json (strict) rejects with a 400.
  const response = await api.post('/api/day-summary/refresh', {}, { params: daySummaryParams(date) })
  return unwrap<DaySummary>(response.data, 'refresh day summary')
}
// Create a day-note. `date` is 'YYYY-MM-DD' (or any ISO the backend can
// parse — it normalises to UTC-midnight date-only). `scope` defaults to
// 'private' server-side when omitted.
export const createDayNoteApi = async (payload: {
  date: string
  body: string
  scope?: DayNoteScope
}): Promise<DayNote> => {
  const response = await api.post('/api/day-notes', payload)
  return unwrap<DayNote>(response.data, 'create day note')
}
// Update a day-note's body and/or scope. Author-only (admins may also
// edit firm-scoped notes) — the backend 403s otherwise.
export const updateDayNoteApi = async (
  id: string,
  payload: { body?: string; scope?: DayNoteScope },
): Promise<DayNote> => {
  const response = await api.patch(`/api/day-notes/${id}`, payload)
  return unwrap<DayNote>(response.data, 'update day note')
}
export const deleteDayNoteApi = async (id: string): Promise<void> => {
  const response = await api.delete(`/api/day-notes/${id}`)
  unwrapVoid(response.data, 'delete day note')
}
// ── REMINDERS API ─────────────────────────────────────────
// Personal calendar day-reminders (GET/POST/PATCH/DELETE /api/reminders),
// all self-scoped server-side. Powers the day-panel "Remind me" editor +
// the grid 🔔 indicator. `remindAt` is an optional ISO datetime.
export const fetchRemindersApi = async (options?: {
  from?: string
  to?: string
}): Promise<Reminder[]> => {
  const params: Record<string, string> = {}
  if (options?.from) params.from = options.from
  if (options?.to) params.to = options.to
  const response = await api.get('/api/reminders', {
    params: Object.keys(params).length ? params : undefined,
  })
  return unwrap<Reminder[]>(response.data, 'load reminders')
}
export const createReminderApi = async (payload: {
  date: string
  text: string
  remindAt?: string | null
}): Promise<Reminder> => {
  const response = await api.post('/api/reminders', payload)
  return unwrap<Reminder>(response.data, 'create reminder')
}
export const updateReminderApi = async (
  id: string,
  payload: { text?: string; remindAt?: string | null },
): Promise<Reminder> => {
  const response = await api.patch(`/api/reminders/${id}`, payload)
  return unwrap<Reminder>(response.data, 'update reminder')
}
export const deleteReminderApi = async (id: string): Promise<void> => {
  const response = await api.delete(`/api/reminders/${id}`)
  unwrapVoid(response.data, 'delete reminder')
}
// ── MICROSOFT INTEGRATION (Stage 1: OAuth connect foundation) ─
export interface MicrosoftStatus {
  connected: boolean
  msEmail: string | null
  connectedAt: string | null
  // A connection is stored but its tokens can no longer be read (e.g. after a
  // server key change) — the user must reconnect.
  needsReconnect?: boolean
}
// Fetch the Microsoft /authorize URL (authenticated), which the caller then
// navigates to (top-level redirect). Session token stays out of the URL.
// `codeChallenge` = base64url(SHA-256(code_verifier)) — PKCE: the verifier
// stays in THIS browser (utils/microsoftPkce) for msCompleteApi.
export const msConnectUrlApi = async (codeChallenge: string): Promise<string> => {
  const response = await api.get('/api/integrations/microsoft/connect', { params: { challenge: codeChallenge } })
  return unwrap<{ url: string }>(response.data, 'start Microsoft connect').url
}
// Finish the connection after Microsoft's redirect (?ms=pending): this tab's
// code_verifier, as the signed-in user. The pending code waits on the server,
// found by the verifier's challenge (nothing redeemable is in the URL).
export const msCompleteApi = async (codeVerifier: string): Promise<{ connected: boolean; msEmail: string }> => {
  const response = await api.post('/api/integrations/microsoft/complete', { codeVerifier })
  return unwrap<{ connected: boolean; msEmail: string }>(response.data, 'complete Microsoft connect')
}
export const msStatusApi = async (): Promise<MicrosoftStatus> => {
  const response = await api.get('/api/integrations/microsoft/status')
  return unwrap<MicrosoftStatus>(response.data, 'load Microsoft status')
}
export const msDisconnectApi = async (): Promise<void> => {
  const response = await api.delete('/api/integrations/microsoft')
  unwrapVoid(response.data, 'disconnect Microsoft')
}
// ── OUTLOOK CALENDAR (Stage 2: Outlook-sync, READ-ONLY) ──────
// A normalized Outlook event mirrored onto the Casevo calendar. Times are ISO
// 8601 UTC. `webLink` opens the event in Outlook (the only action Casevo
// offers — these can't be edited/moved/deleted here).
export interface OutlookEvent {
  id: string
  title: string
  start: string        // ISO 8601 UTC
  end: string          // ISO 8601 UTC
  isAllDay: boolean
  location: string | null
  webLink: string | null
  showAs: string | null
}
export interface OutlookCalendarResult {
  connected: boolean
  needsReconnect?: boolean
  events: OutlookEvent[]
}
// Fetch the connected user's Outlook events for [from,to] (GET
// /api/integrations/microsoft/calendar). Always graceful server-side: when not
// connected it returns { connected:false, events:[] }; a dead token returns
// needsReconnect. Personal — always the caller's own mailbox. The server
// caches a read for 60 s per user+range; `fresh` (focus / poll / retry
// refresh) asks it to skip that cached copy.
export const fetchOutlookEventsApi = async (options: {
  from: string
  to: string
  fresh?: boolean
}): Promise<OutlookCalendarResult> => {
  const response = await api.get('/api/integrations/microsoft/calendar', {
    params: options.fresh ? { from: options.from, to: options.to, fresh: '1' } : { from: options.from, to: options.to },
  })
  return unwrap<OutlookCalendarResult>(response.data, 'load Outlook calendar')
}
// ── EMAIL-SEND (Stage 3) ─────────────────────────────────────
// Send an email AS the user's own Microsoft mailbox + log it as an activity.
// Returns a discriminated result rather than throwing, so the compose modal
// can branch on needsConnect / needsReconnect (prompt the user) vs a generic
// error (keep the draft) without unwrapping an axios error at the call site.
export interface SendMailResult {
  ok: boolean
  needsConnect?: boolean
  needsReconnect?: boolean
  // Stage B: attachments busted the ~3MB inline budget. The compose modal keeps
  // the draft and shows the (over-budget) message so the user can trim files.
  tooLarge?: boolean
  error?: string
}
export const sendOutlookMailApi = async (payload: {
  to: string
  cc?: string
  bcc?: string
  subject: string
  bodyHtml: string
  priority?: 'low' | 'normal' | 'high'
  readReceipt?: boolean
  durationSeconds?: number
  correspondentType?: string
  // Stage B: ids of the case's FileAttachment rows to attach inline.
  attachmentFileIds?: string[]
  // Stage 1 share links: filenames sent as a secure LINK (in the body) rather
  // than attached — noted on the logged activity.
  sharedFilenames?: string[]
  caseId?: string | null
  clientId?: string | null
}): Promise<SendMailResult> => {
  try {
    const response = await api.post('/api/integrations/microsoft/send', payload)
    unwrapVoid(response.data, 'send email')
    return { ok: true }
  } catch (err) {
    // The backend signals connect/reconnect via a 400 body; axios throws on
    // non-2xx, so read the response body to distinguish those from a generic
    // failure (502 / network) that should just keep the draft + show a message.
    if (axios.isAxiosError(err) && err.response?.data && typeof err.response.data === 'object') {
      const data = err.response.data as { needsConnect?: boolean; needsReconnect?: boolean; tooLarge?: boolean; error?: string }
      if (data.needsConnect) return { ok: false, needsConnect: true, error: data.error }
      if (data.needsReconnect) return { ok: false, needsReconnect: true, error: data.error }
      if (data.tooLarge) return { ok: false, tooLarge: true, error: data.error }
      if (typeof data.error === 'string') return { ok: false, error: data.error }
    }
    return { ok: false, error: err instanceof Error ? err.message : 'Failed to send email' }
  }
}
// ── EMAIL DRAFTS (Stage E) ─────────────────────────────────
// A saved, resumable compose. All endpoints are user-scoped server-side (a
// user only ever sees/edits their own). The fields mirror the compose modal so
// a draft round-trips exactly.
export interface EmailDraft {
  id: string
  userId: string
  firmId: string
  clientId: string | null
  caseId: string | null
  to: string | null
  cc: string | null
  bcc: string | null
  subject: string | null
  bodyHtml: string | null
  correspondentType: string | null
  priority: string | null
  durationSeconds: number
  attachmentFileIds: string[]
  createdAt: string
  updatedAt: string
}
export type EmailDraftInput = {
  clientId?: string | null
  caseId?: string | null
  to?: string
  cc?: string
  bcc?: string
  subject?: string
  bodyHtml?: string
  correspondentType?: string
  priority?: string
  durationSeconds?: number
  attachmentFileIds?: string[]
}
export const createEmailDraftApi = async (input: EmailDraftInput): Promise<EmailDraft> => {
  const response = await api.post('/api/email-drafts', input)
  return unwrap<EmailDraft>(response.data, 'save draft')
}
export const updateEmailDraftApi = async (id: string, input: EmailDraftInput): Promise<EmailDraft> => {
  const response = await api.put(`/api/email-drafts/${id}`, input)
  return unwrap<EmailDraft>(response.data, 'update draft')
}
export const listEmailDraftsApi = async (opts?: { caseId?: string; clientId?: string }): Promise<EmailDraft[]> => {
  const params: Record<string, string> = {}
  if (opts?.caseId) params.caseId = opts.caseId
  if (opts?.clientId) params.clientId = opts.clientId
  const response = await api.get('/api/email-drafts', { params: Object.keys(params).length ? params : undefined })
  return unwrap<EmailDraft[]>(response.data, 'load drafts')
}
export const getEmailDraftApi = async (id: string): Promise<EmailDraft> => {
  const response = await api.get(`/api/email-drafts/${id}`)
  return unwrap<EmailDraft>(response.data, 'load draft')
}
export const deleteEmailDraftApi = async (id: string): Promise<void> => {
  const response = await api.delete(`/api/email-drafts/${id}`)
  unwrapVoid(response.data, 'delete draft')
}
// ── APPOINTMENTS API ──────────────────────────────────────
// Calendar appointments (meetings/court/calls). The calendar itself reads
// them via /api/calendar (type:'appointment'); these are the CRUD helpers
// for the create/edit form. list is available for a standalone window read.
export const fetchAppointmentsApi = async (
  options?: { from?: string; to?: string; scope?: 'mine' | 'firm' },
): Promise<Appointment[]> => {
  const params: Record<string, string> = {}
  if (options?.from) params.from = options.from
  if (options?.to) params.to = options.to
  if (options?.scope) params.scope = options.scope
  const response = await api.get('/api/appointments', {
    params: Object.keys(params).length ? params : undefined,
  })
  return unwrap<Appointment[]>(response.data, 'load appointments')
}
export const getAppointmentApi = async (id: string): Promise<Appointment> => {
  const response = await api.get(`/api/appointments/${id}`)
  return unwrap<Appointment>(response.data, 'load appointment')
}
export const createAppointmentApi = async (payload: CreateAppointmentPayload): Promise<Appointment> => {
  const response = await api.post('/api/appointments', payload)
  const created = unwrap<Appointment>(response.data, 'create appointment')
  // Same-tab signal (entity-changed is echo-suppressed for the actor).
  publishLocalEntityChange({ entity: 'appointment', op: 'create', id: created.id })
  return created
}
export const updateAppointmentApi = async (
  id: string,
  payload: UpdateAppointmentPayload,
  // Recurrence Stage 2: 'occurrence' edits only this occurrence (an override
  // row) when id is a composite "<seriesId>:<date>"; 'series' (default) edits
  // the whole series/parent.
  applyTo: 'series' | 'occurrence' = 'series',
): Promise<Appointment> => {
  const response = await api.patch(`/api/appointments/${id}`, { ...payload, applyTo })
  const updated = unwrap<Appointment>(response.data, 'update appointment')
  publishLocalEntityChange({ entity: 'appointment', op: 'update', id })
  return updated
}
// Drag-move a RECURRING occurrence with the standard scope prompt:
// 'occurrence' = this event only (a series-locked override — no duplicate
// rows), 'following' = this and following (splits the series), 'series' = all
// events (shifts the whole series). id = the dragged occurrence id.
export type RecurringMoveScope = 'occurrence' | 'following' | 'series'
export const moveAppointmentApi = async (
  id: string,
  applyTo: RecurringMoveScope,
  startAt: string,
  endAt: string | null,
): Promise<void> => {
  const response = await api.post(`/api/appointments/${id}/move`, { applyTo, startAt, endAt })
  unwrapVoid(response.data, 'move appointment')
  publishLocalEntityChange({ entity: 'appointment', op: 'update', id })
}
export const deleteAppointmentApi = async (
  id: string,
  applyTo: 'series' | 'occurrence' = 'series',
): Promise<void> => {
  const response = await api.delete(`/api/appointments/${id}`, { params: { applyTo } })
  unwrapVoid(response.data, 'delete appointment')
  publishLocalEntityChange({ entity: 'appointment', op: 'delete', id })
}
// ── DOCUMENT SEARCH API CALLS ─────────────────────────────
// Filename search across the firm's uploaded files (FileAttachment
// table). All three filters are optional and AND together on the
// backend. Lazy params: empty filter set sends NO query string.
export const fetchDocumentSearchApi = async (
  options?: { q?: string; caseId?: string; clientId?: string },
): Promise<DocumentSearchResult[]> => {
  const params: Record<string, string> = {}
  if (options?.caseId) params.caseId = options.caseId
  if (options?.clientId) params.clientId = options.clientId
  const response = await api.get('/api/documents/search', {
    params: Object.keys(params).length ? params : undefined,
    headers: searchHeaders(options?.q),
  })
  return unwrap<DocumentSearchResult[]>(response.data, 'search documents')
}
// ── TASKS API ─────────────────────────────────────────────
// Phase 2f. Mirrors clientsApi / documentSearchApi patterns —
// optional filters via query params, unwrap<T> envelope.
export const listTasksApi = async (filters?: ListTasksFilters): Promise<Task[]> => {
  const params: Record<string, string> = {}
  if (filters?.assignedToUserId) params.assignedToUserId = filters.assignedToUserId
  if (filters?.status) params.status = filters.status
  if (filters?.priority) params.priority = filters.priority
  if (filters?.dueFrom) params.dueFrom = filters.dueFrom
  if (filters?.dueTo) params.dueTo = filters.dueTo
  if (filters?.unclaimed) params.unclaimed = 'true'
  if (filters?.createdByMe) params.createdByMe = 'true'
  const response = await api.get('/api/tasks', { params })
  return unwrap<Task[]>(response.data, 'list tasks')
}
// ★ My work summary — the caller's "done this week" + (gated) team load.
// weekStart/today are the USER's local week/day so counts match the page.
export const getTaskSummaryApi = async (opts: { weekStart: string; today: string }): Promise<TaskSummary> => {
  const response = await api.get('/api/tasks/summary', { params: opts })
  return unwrap<TaskSummary>(response.data, 'load task summary')
}
// Single task by id (GET /api/tasks/:id — backend endpoint already exists,
// firm-scoped + assignee-scoped for non-admins). Used by the task autosave's
// COLUMN-AWARE 409 recovery to re-fetch the row and compare touched columns,
// since listTasksApi returns a FILTERED/scoped list that may omit the edited
// task. Returns the single Task row.
export const getTaskByIdApi = async (taskId: string): Promise<Task> => {
  const response = await api.get(`/api/tasks/${taskId}`)
  return unwrap<Task>(response.data, 'fetch task')
}
export const createTaskApi = async (payload: CreateTaskPayload): Promise<Task> => {
  const response = await api.post('/api/tasks', payload)
  const created = unwrap<Task>(response.data, 'create task')
  // Same-tab signal (entity-changed is echo-suppressed for the actor).
  publishLocalEntityChange({ entity: 'task', op: 'create', id: created.id })
  return created
}
export const updateTaskApi = async (taskId: string, payload: UpdateTaskPayload): Promise<Task> => {
  const response = await api.patch(`/api/tasks/${taskId}`, payload)
  const updated = unwrap<Task>(response.data, 'update task')
  publishLocalEntityChange({ entity: 'task', op: 'update', id: taskId })
  return updated
}
export const deleteTaskApi = async (taskId: string): Promise<void> => {
  const response = await api.delete(`/api/tasks/${taskId}`)
  if (!response.data?.success) {
    throw new Error(response.data?.error || 'Failed to delete task')
  }
  publishLocalEntityChange({ entity: 'task', op: 'delete', id: taskId })
}
// ── ACTIVITIES API ────────────────────────────────────────
// Per-client billable timeline. Member-writable (any firm member;
// author-or-admin on edit/delete). List returns a bundled `total`
// sibling (sum of amountPence + durationSeconds + count) aggregated
// over the SAME where clause — so total always reflects "sum of
// what you're currently viewing". Reads the sibling directly off
// response.data via the updateUserRoleApi.note pattern (defensive
// typeof narrowing + 0 fallback since Prisma _sum returns null
// server-side for zero rows).
export const listActivitiesApi = async (
  filters?: {
    clientId?: string
    caseId?: string
    // Stage 1: filter to a specific actor's entries. The literal
    // 'me' is a server-side sentinel that resolves to req.user.userId
    // — pass 'me' rather than reading the store's user id client-side
    // so a stale/mismatched cached user can't leak the wrong bucket.
    actorUserId?: string
    // Stage 1 (timeline filters): filter to a single actionType. The
    // server does an exact match, so pass one of the live vocab values
    // ('call' | 'take_call' | 'letter' | 'memo'). Omit / '' → no
    // actionType narrowing. An unknown value returns an empty (but
    // correctly-totalled) set rather than erroring.
    actionType?: string
    // Stage 3: correspondentType exact match. IMPORTANT: '' is a
    // MEANINGFUL value (filter to untagged activities), so it is sent
    // verbatim; only `undefined` omits the param. That's why the
    // threading below uses `!== undefined` rather than truthiness.
    correspondentType?: string
    // Stage 1: inclusive createdAt range bounds. Pass ISO timestamps
    // (Date#toISOString()). Server treats them as UTC and silently
    // drops any bound that fails Date.parse (rather than 500'ing),
    // so a partially-invalid range still returns the best-effort
    // subset.
    from?: string
    to?: string
  },
): Promise<{ activities: Activity[]; total: ActivityTotal }> => {
  const params: Record<string, string> = {}
  if (filters?.clientId) params.clientId = filters.clientId
  if (filters?.caseId) params.caseId = filters.caseId
  if (filters?.actorUserId) params.actorUserId = filters.actorUserId
  if (filters?.actionType) params.actionType = filters.actionType
  // Sent even when '' — empty string means "untagged" (no correspondent).
  if (filters?.correspondentType !== undefined) params.correspondentType = filters.correspondentType
  if (filters?.from) params.from = filters.from
  if (filters?.to) params.to = filters.to
  // axios URL-encodes each params entry automatically, so ISO
  // timestamp colons and the '+' offset don't need manual escaping.
  const response = await api.get('/api/activities', { params })
  const activities = unwrap<Activity[]>(response.data, 'load activities')
  const raw = response.data?.total
  const total: ActivityTotal = {
    amountPence: typeof raw?.amountPence === 'number' ? raw.amountPence : 0,
    durationSeconds: typeof raw?.durationSeconds === 'number' ? raw.durationSeconds : 0,
    count: typeof raw?.count === 'number' ? raw.count : 0,
  }
  return { activities, total }
}
export const getActivityByIdApi = async (id: string): Promise<Activity> => {
  const response = await api.get(`/api/activities/${id}`)
  return unwrap<Activity>(response.data, 'load activity')
}
// ── COLOUR SCHEME API (action-type colour cascade) ──────────
// Firm house colours — any firm member may read. Returns the stored
// { actionType: token } map, or {} when unset. (Admin-only WRITE lands
// with the Settings UI in a later stage.)
export const getFirmColourSchemeApi = async (): Promise<Record<string, string>> => {
  const response = await api.get('/api/firm/colour-scheme')
  const data = unwrap<{ scheme?: Record<string, string> }>(response.data, 'load firm colour scheme')
  return data.scheme ?? {}
}
// Admin-only WRITE — PUT the firm scheme ({ actionType: token }). Backend
// validates every key is a known actionType + every value a known palette
// token (400 otherwise). Returns the saved scheme.
export const putFirmColourSchemeApi = async (
  scheme: Record<string, string>,
): Promise<Record<string, string>> => {
  const response = await api.put('/api/firm/colour-scheme', { scheme })
  const data = unwrap<{ scheme?: Record<string, string> }>(response.data, 'save firm colour scheme')
  return data.scheme ?? {}
}
// The current user's colour scheme — BOTH the LIVE scheme (currentScheme,
// the top layer of the cascade) AND their saved personal template
// (savedTemplate, restorable via the Settings panel). Either is {} when
// unset. The store keeps only currentScheme for rendering; the Settings
// panel needs savedTemplate too (to enable/disable "Restore my template").
export interface UserColourScheme {
  currentScheme: Record<string, string>
  savedTemplate: Record<string, string>
}
export const getUserColourSchemeApi = async (): Promise<UserColourScheme> => {
  const response = await api.get('/api/me/colour-scheme')
  const data = unwrap<{ currentScheme?: Record<string, string>; savedTemplate?: Record<string, string> }>(
    response.data,
    'load your colour scheme',
  )
  return { currentScheme: data.currentScheme ?? {}, savedTemplate: data.savedTemplate ?? {} }
}
// Self WRITE — PUT the user's currentScheme, and OPTIONALLY savedTemplate.
// Omit savedTemplate (pass undefined) to leave the stored template
// untouched; pass an object to overwrite it. Backend validates both as
// { actionType: token } maps. Returns the saved { currentScheme, savedTemplate }.
export const putUserColourSchemeApi = async (
  currentScheme: Record<string, string>,
  savedTemplate?: Record<string, string>,
): Promise<UserColourScheme> => {
  const body: { currentScheme: Record<string, string>; savedTemplate?: Record<string, string> } = { currentScheme }
  if (savedTemplate !== undefined) body.savedTemplate = savedTemplate
  const response = await api.put('/api/me/colour-scheme', body)
  const data = unwrap<{ currentScheme?: Record<string, string>; savedTemplate?: Record<string, string> }>(
    response.data,
    'save your colour scheme',
  )
  return { currentScheme: data.currentScheme ?? {}, savedTemplate: data.savedTemplate ?? {} }
}

// ── FIRM SETTINGS API (case history visibility) ─────────────
// Who (besides admins) may view a case's History section:
//   "admin"    — admins only
//   "assignee" — admins + the case's assigned user
//   "firm"     — any firm member
export type CaseHistoryVisibility = 'admin' | 'assignee' | 'firm'
// Member-readable — returns the firm's current value (default "assignee").
export const getCaseHistoryVisibilityApi = async (): Promise<CaseHistoryVisibility> => {
  const response = await api.get('/api/firm/case-history-visibility')
  const data = unwrap<{ value?: CaseHistoryVisibility }>(response.data, 'load case history visibility')
  return data.value ?? 'assignee'
}
// Admin-only WRITE. Backend validates value ∈ {admin, assignee, firm}
// (400 otherwise) and scopes to the caller's firm. Returns the saved value.
export const putCaseHistoryVisibilityApi = async (
  value: CaseHistoryVisibility,
): Promise<CaseHistoryVisibility> => {
  const response = await api.put('/api/firm/case-history-visibility', { value })
  const data = unwrap<{ value?: CaseHistoryVisibility }>(response.data, 'save case history visibility')
  return data.value ?? 'assignee'
}

// ── FIRM SETTINGS API (task-completion notification routing) ─
// Who gets notified when a task is completed by someone other than
// its creator:
//   "creator"            — the task creator only
//   "creator_and_admins" — the creator + every firm admin
//   "admins"             — every firm admin only
export type TaskCompleteNotify = 'creator' | 'creator_and_admins' | 'admins'
// Member-readable — returns the firm's current value (default "creator").
export const getTaskCompleteNotifyApi = async (): Promise<TaskCompleteNotify> => {
  const response = await api.get('/api/firm/task-complete-notify')
  const data = unwrap<{ value?: TaskCompleteNotify }>(response.data, 'load task completion routing')
  return data.value ?? 'creator'
}
// Admin-only WRITE. Backend validates value ∈ {creator, creator_and_admins,
// admins} (400 otherwise) and scopes to the caller's firm. Returns saved value.
export const putTaskCompleteNotifyApi = async (
  value: TaskCompleteNotify,
): Promise<TaskCompleteNotify> => {
  const response = await api.put('/api/firm/task-complete-notify', { value })
  const data = unwrap<{ value?: TaskCompleteNotify }>(response.data, 'save task completion routing')
  return data.value ?? 'creator'
}

// ── FIRM 2FA ENFORCEMENT ────────────────────────────────────
// require2fa: when true, every firm member must have 2FA enabled to use
// the app. Member-readable (the app-load gate needs it); admin-only write.
// Member-readable — returns the firm's current flag (default false).
// ── Firm BILLING settings (Invoicing Stage 0) — ADMIN-ONLY read + write ──
// ★ Backed by NEW Firm columns: these 500 until Reuben's schema push is live.
export interface FirmBillingSettings {
  vatNumber: string | null
  sraNumber: string | null
  invoiceNumberFormat: string | null
  nextInvoiceSeq: number
  // Invoicing Stage 2 — credit notes number separately.
  creditNoteNumberFormat: string | null
  nextCreditNoteSeq: number
  defaultCreditNoteNumberFormat: string
  // How many of each the firm has numbered so far. 0 → "start numbering from"
  // is freely editable; > 0 → it can only move forward (server-enforced).
  invoicesIssued: number
  creditNotesIssued: number
  billApprovalRequired: boolean
  defaultSuccessPercent: number | null
  defaultInvoiceNumberFormat: string
  // Invoicing Stage 1 — letterhead + payment details printed on bills.
  billingAddress: string | null
  billingPhone: string | null
  billingEmail: string | null
  bankAccountName: string | null
  bankSortCode: string | null
  bankAccountNumber: string | null
  paymentTermsDays: number
  complaintsContact: string | null
  // (B3b) The firm-level signatory / signature image are gone — each user has
  // their own (getMySignatureApi / getUserSignatureApi).
  // Firm logo — opaque id of the CURRENT logo (null = none). Its image comes
  // from getFirmLogoByIdApi (utils/firmLogo). Changed only by a PATCH (Save).
  logoId: string | null
  // Document colour ("#rrggbb") — the accent of the firm's documents
  // (utils/documentTheme). null = the Casevo default (defaultAccentColour).
  accentColour: string | null
  defaultAccentColour: string
  // Optional secondary document colour ("#rrggbb" — pills, table headers,
  // divider rules, balance box). null = derived from the accent.
  secondaryColour: string | null
  // Document fonts — a DOCUMENT_FONTS id (utils/documentFonts). null = the
  // default (defaultDocumentFont).
  headingFont: string | null
  bodyFont: string | null
  defaultDocumentFont: string
  // Send bill — the covering email template (null = the default below).
  billEmailSubject: string | null
  billEmailBody: string | null
  defaultBillEmailSubject: string
  defaultBillEmailBody: string
  billMergeFields: string[]
}
export type FirmBillingSettingsPatch = Partial<Pick<FirmBillingSettings, 'vatNumber' | 'sraNumber' | 'invoiceNumberFormat' | 'creditNoteNumberFormat' | 'billApprovalRequired' | 'defaultSuccessPercent' | 'billingAddress' | 'billingPhone' | 'billingEmail' | 'bankAccountName' | 'bankSortCode' | 'bankAccountNumber' | 'paymentTermsDays' | 'complaintsContact' | 'nextInvoiceSeq' | 'nextCreditNoteSeq' | 'billEmailSubject' | 'billEmailBody' | 'logoId' | 'accentColour' | 'secondaryColour' | 'headingFont' | 'bodyFont'>>
export const getBillingSettingsApi = async (): Promise<FirmBillingSettings> => {
  const response = await api.get('/api/firm/billing-settings')
  return unwrap<FirmBillingSettings>(response.data, 'load billing settings')
}
export const patchBillingSettingsApi = async (patch: FirmBillingSettingsPatch): Promise<FirmBillingSettings> => {
  const response = await api.patch('/api/firm/billing-settings', patch)
  return unwrap<FirmBillingSettings>(response.data, 'save billing settings')
}
// B3b — a user's OWN signature image: the avatar presigned-PUT flow
// (request URL → PUT to R2 → confirm). userId omitted = your own
// (/api/me/signature); given = an admin managing a team member (Admin › Team).
const signaturePath = (userId?: string): string => (userId ? `/api/users/${userId}/signature` : '/api/me/signature')
export const getUserSignatureApi = async (userId?: string): Promise<UserSignature> =>
  unwrap<UserSignature>((await api.get(signaturePath(userId))).data, 'load the signature')
export const requestSignatureUploadUrlApi = async (params: { mimeType: string; sizeBytes: number }, userId?: string): Promise<{ uploadUrl: string; r2Key: string }> =>
  unwrap<{ uploadUrl: string; r2Key: string }>((await api.post(`${signaturePath(userId)}/upload-url`, params)).data, 'prepare the upload')
export const confirmSignatureApi = async (r2Key: string, userId?: string): Promise<UserSignature> =>
  unwrap<UserSignature>((await api.post(`${signaturePath(userId)}/confirm`, { r2Key })).data, 'save the signature')
export const removeSignatureApi = async (userId?: string): Promise<UserSignature> =>
  unwrap<UserSignature>((await api.delete(signaturePath(userId))).data, 'remove the signature')
// B3b — the old firm-level signature image, and the one-time "move it to a user".
export const getFirmSignatureApi = async (): Promise<FirmSignature> =>
  unwrap<FirmSignature>((await api.get('/api/firm/billing-settings/firm-signature')).data, 'load the firm signature')
export const moveFirmSignatureApi = async (userId: string): Promise<FirmSignature> =>
  unwrap<FirmSignature>((await api.post('/api/firm/billing-settings/firm-signature/move', { userId })).data, 'move the signature')
// Firm logo — every firm PDF / document's letterhead. Upload is admin-only
// (server-enforced); the image BYTES go through the server, which checks them
// (PNG / JPEG by content, 1 MB) before storing a new R2 key. Uploading does
// NOT change the firm's logo — it returns the stored file's id, which becomes
// the logo when Save billing settings sends it (patch.logoId; null removes).
export const uploadFirmLogoApi = async (file: Blob): Promise<{ logoId: string; dataUri: string }> => {
  const response = await api.post('/api/firm/logo', file, { headers: { 'Content-Type': file.type || 'application/octet-stream' } })
  return unwrap<{ logoId: string; dataUri: string }>(response.data, 'upload logo')
}
// Any member: the firm's CURRENT document branding for the case summary
// export — its logo (null / null = none) + colours and fonts (null = default).
export interface FirmDocumentBranding {
  logoId: string | null; dataUri: string | null
  accentColour?: string | null; secondaryColour?: string | null; headingFont?: string | null; bodyFont?: string | null
}
export const getFirmLogoApi = async (): Promise<FirmDocumentBranding> => {
  const response = await api.get('/api/firm/logo')
  return unwrap<FirmDocumentBranding>(response.data, 'load logo')
}
// Any member: one of the firm's logos by id (a bill's letterhead names its own).
export const getFirmLogoByIdApi = async (logoId: string): Promise<string> => {
  const response = await api.get(`/api/firm/logos/${encodeURIComponent(logoId)}`)
  return unwrap<{ dataUri: string }>(response.data, 'load logo').dataUri
}
// (Big-audit batch 4, M8) The firm's AI daily briefing setting.
export const getAiDailyBriefingApi = async (): Promise<boolean> => {
  const response = await api.get('/api/firm/ai-daily-briefing')
  return unwrap<{ value: boolean }>(response.data, 'load the AI daily briefing setting').value
}
export const setAiDailyBriefingApi = async (value: boolean): Promise<boolean> => {
  const response = await api.put('/api/firm/ai-daily-briefing', { value })
  return unwrap<{ value: boolean }>(response.data, 'save the AI daily briefing setting').value
}
// (Big-audit batch 4, item 11) Does a client this user can't see have this exact name?
export const checkRestrictedClientNameApi = async (forename: string, surname: string): Promise<boolean> => {
  const response = await api.post('/api/clients/name-check', { forename, surname })
  return unwrap<{ restrictedMatch: boolean }>(response.data, 'check the client name').restrictedMatch
}
// …the same for each row of a bulk import: a yes/no per name.
export const checkRestrictedClientNamesApi = async (names: { forename: string; surname: string }[]): Promise<boolean[]> => {
  const response = await api.post('/api/clients/name-check', { names })
  return unwrap<{ restrictedMatches: boolean[] }>(response.data, 'check the client names').restrictedMatches
}
// Admin › Security: the firm's "Two-person checks" switch (ON unless turned off).
export const getTwoPersonChecksApi = async (): Promise<boolean> =>
  unwrap<{ value?: boolean }>((await api.get('/api/firm/two-person-checks')).data, 'load the two-person checks setting').value !== false
export const setTwoPersonChecksApi = async (value: boolean): Promise<boolean> =>
  unwrap<{ value?: boolean }>((await api.put('/api/firm/two-person-checks', { value })).data, 'save the two-person checks setting').value !== false
export const getRequire2faApi = async (): Promise<boolean> => {
  const response = await api.get('/api/firm/require-2fa')
  const data = unwrap<{ value?: boolean }>(response.data, 'load 2FA requirement')
  return data.value === true
}
// Admin-only WRITE. Backend validates value is a boolean (400 otherwise)
// and scopes to the caller's firm. Returns the saved value.
export const setRequire2faApi = async (value: boolean): Promise<boolean> => {
  const response = await api.put('/api/firm/require-2fa', { value })
  const data = unwrap<{ value?: boolean }>(response.data, 'save 2FA requirement')
  return data.value === true
}

// ── FAVOURITES API (sidebar Favourites) ─────────────────────
// Self-only — the backend scopes every op to req.user.userId from the
// JWT, so no userId is ever sent from the client.
export type FavouriteItemType = 'case' | 'client' | 'task'
export interface Favourite {
  id: string
  itemType: FavouriteItemType
  itemId: string
  subSection: string | null
  label: string
  sortOrder: number
  createdAt: string
}
// GET — the caller's favourites, ordered (sortOrder asc, then createdAt asc).
export const listFavouritesApi = async (): Promise<Favourite[]> => {
  const response = await api.get('/api/me/favourites')
  return unwrap<Favourite[]>(response.data, 'load favourites')
}
// POST — pin an item. Re-pinning the same item is safe (backend upserts,
// refreshing the label); a new item appends to the end. Returns the row.
export const addFavouriteApi = async (payload: {
  itemType: FavouriteItemType
  itemId: string
  label: string
  subSection?: string | null
}): Promise<Favourite> => {
  const response = await api.post('/api/me/favourites', payload)
  return unwrap<Favourite>(response.data, 'add favourite')
}
// DELETE — unpin by id. Backend only removes it when it belongs to the
// caller (404 otherwise). No response body on success.
export const removeFavouriteApi = async (id: string): Promise<void> => {
  const response = await api.delete(`/api/me/favourites/${id}`)
  unwrapVoid(response.data, 'remove favourite')
}
// PUT — reorder. Send the ids in the new order; the backend sets each
// row's sortOrder to its index (own rows only) and returns the new list.
export const reorderFavouritesApi = async (orderedIds: string[]): Promise<Favourite[]> => {
  const response = await api.put('/api/me/favourites/reorder', { orderedIds })
  return unwrap<Favourite[]>(response.data, 'reorder favourites')
}

// ── 2FA (TOTP) ENROLMENT ──────────────────────────────────
// Stage 1: enrolment only (login is untouched). All self-scoped — the
// backend reads userId from the JWT. setup returns the QR + secret to
// key into an authenticator app; confirm verifies the first code and
// returns the one-time backup codes EXACTLY ONCE; status is a lightweight
// enabled flag for the settings UI. No call ever returns backup codes
// again, and the secret is only returned by setup (enrolment material).
export const setup2faApi = async (): Promise<{ qrDataUrl: string; secret: string; otpauthUri: string }> => {
  const response = await api.post('/api/me/2fa/setup')
  return unwrap<{ qrDataUrl: string; secret: string; otpauthUri: string }>(response.data, 'start 2FA setup')
}
export const confirm2faApi = async (token: string): Promise<{ enabled: boolean; backupCodes: string[] }> => {
  const response = await api.post('/api/me/2fa/confirm', { token })
  return unwrap<{ enabled: boolean; backupCodes: string[] }>(response.data, 'confirm 2FA')
}
export const get2faStatusApi = async (): Promise<{ enabled: boolean }> => {
  const response = await api.get('/api/me/2fa/status')
  return unwrap<{ enabled: boolean }>(response.data, 'load 2FA status')
}
// ── AVATAR PHOTO (self) ───────────────────────────────────
// Client-side crop → presigned PUT → confirm. Step 1: get a presigned
// PUT URL + the server-chosen key. Step 2: the browser PUTs the cropped
// blob straight to R2 (plain fetch, Content-Type only — same as
// FileUpload). Step 3: confirm the key → server sets it + returns a
// fresh presigned GET URL. removeAvatarApi clears it.
export const requestAvatarUploadUrlApi = async (params: { mimeType: string; sizeBytes: number }): Promise<{ uploadUrl: string; r2Key: string }> => {
  const response = await api.post('/api/me/avatar/upload-url', params)
  return unwrap<{ uploadUrl: string; r2Key: string }>(response.data, 'request avatar upload URL')
}
export const confirmAvatarApi = async (r2Key: string): Promise<{ avatarPhotoUrl: string | null }> => {
  const response = await api.post('/api/me/avatar/confirm', { r2Key })
  return unwrap<{ avatarPhotoUrl: string | null }>(response.data, 'confirm avatar')
}
export const removeAvatarApi = async (): Promise<{ avatarPhotoUrl: string | null }> => {
  const response = await api.delete('/api/me/avatar')
  return unwrap<{ avatarPhotoUrl: string | null }>(response.data, 'remove avatar')
}
// Disable 2FA (self). Requires a CURRENT code (TOTP or backup) to
// re-verify the caller holds the second factor. Returns { enabled:false }.
export const disable2faApi = async (token: string): Promise<{ enabled: boolean }> => {
  const response = await api.post('/api/me/2fa/disable', { token })
  return unwrap<{ enabled: boolean }>(response.data, 'disable 2FA')
}
// Admin: reset a firm user's 2FA (lockout recovery). Admin-only +
// firm-scoped server-side. No code needed — the admin gate is the
// protection. Returns { reset: true }.
// Login-email change. The user can only REQUEST (notifies firm admins; no
// storage); an admin actions it (firm-scoped, uniqueness-checked, revokes the
// member's sessions). See backend emailChangeController.ts.
export const requestEmailChangeApi = async (newEmail: string): Promise<{ notifiedAdmins: number }> => {
  const response = await api.post('/api/me/email-change-request', { newEmail })
  return unwrap<{ notifiedAdmins: number }>(response.data, 'send the request')
}
export const adminChangeUserEmailApi = async (
  userId: string,
  newEmail: string,
): Promise<{ id: string; email: string; sessionsRevoked: boolean; selfChange: boolean }> => {
  const response = await api.patch(`/api/users/${userId}/email`, { newEmail })
  return unwrap<{ id: string; email: string; sessionsRevoked: boolean; selfChange: boolean }>(response.data, 'change email')
}
export const adminReset2faApi = async (userId: string): Promise<{ reset: boolean }> => {
  const response = await api.post(`/api/users/${userId}/2fa/reset`)
  return unwrap<{ reset: boolean }>(response.data, 'reset user 2FA')
}
// ── GRANULAR PERMISSIONS (admin-only) ─────────────────────────
// All firm-scoped + admin-guarded on the backend. Overrides are tristate:
// true=Granted, false=Denied, null (in a PATCH) = clear → inherit firm default.
export const getUserPermissionsApi = async (userId: string): Promise<UserPermissionOverrides> => {
  const response = await api.get(`/api/admin/users/${userId}/permissions`)
  return unwrap<{ overrides: UserPermissionOverrides }>(response.data, 'load user permissions').overrides
}
export const setUserPermissionsApi = async (
  userId: string,
  overrides: Partial<Record<PermissionKey, boolean | null>>,
): Promise<UserPermissionOverrides> => {
  const response = await api.patch(`/api/admin/users/${userId}/permissions`, { overrides })
  return unwrap<{ overrides: UserPermissionOverrides }>(response.data, 'update user permissions').overrides
}
export const getFirmPermissionDefaultsApi = async (): Promise<EffectivePermissions> => {
  const response = await api.get('/api/admin/firm/permission-defaults')
  return unwrap<{ defaults: EffectivePermissions }>(response.data, 'load firm permission defaults').defaults
}
export const setFirmPermissionDefaultsApi = async (
  defaults: Partial<Record<PermissionKey, boolean>>,
): Promise<EffectivePermissions> => {
  const response = await api.patch('/api/admin/firm/permission-defaults', { defaults })
  return unwrap<{ defaults: EffectivePermissions }>(response.data, 'update firm permission defaults').defaults
}
export const createActivityApi = async (payload: CreateActivityPayload): Promise<Activity> => {
  const response = await api.post('/api/activities', payload)
  return unwrap<Activity>(response.data, 'create activity')
}
// Server whitelists action/content/durationSeconds/caseId; anything
// else in the payload is silently stripped. If durationSeconds
// changes, the server recomputes amountPence from the row's FROZEN
// hourlyRatePenceAtEntry — don't send amountPence.
export const updateActivityApi = async (id: string, payload: UpdateActivityPayload): Promise<Activity> => {
  const response = await api.patch(`/api/activities/${id}`, payload)
  return unwrap<Activity>(response.data, 'update activity')
}
// ADMIN-ONLY: re-price unbilled time entries at the CURRENT grade rate
// (backend activityController.repriceActivities). Default scope = £0 entries;
// pass activityIds for specific entries. dryRun → preview only (no writes).
export interface RepriceResult {
  count: number
  fromPence: number
  toPence: number
  zeroRateGrades: string[]
  applied: boolean
  skipped?: number
}
export const repriceActivitiesApi = async (opts: { activityIds?: string[]; dryRun?: boolean } = {}): Promise<RepriceResult> => {
  const response = await api.post('/api/activities/reprice', opts)
  return unwrap<RepriceResult>(response.data, 're-price entries')
}
// Manual success check — DELETE returns { success, message } with
// no data, so unwrap<T> would throw on the missing data field.
// Mirrors deleteTaskApi above.
export const deleteActivityApi = async (id: string): Promise<void> => {
  const response = await api.delete(`/api/activities/${id}`)
  if (!response.data?.success) {
    throw new Error(response.data?.error || 'Failed to delete activity')
  }
}
// ── DEADLINE TYPES API ────────────────────────────────────
// Firm-scoped lookup table for deadline categories. 3 defaults
// seeded at firm creation ("Limitation date", "Trial date",
// "Court issue date"); solicitors can add custom types via POST.
// Backend 409s on duplicate name within a firm.
export const listDeadlineTypesApi = async (): Promise<DeadlineType[]> => {
  const response = await api.get('/api/deadline-types')
  return unwrap<DeadlineType[]>(response.data, 'load deadline types')
}
export const createDeadlineTypeApi = async (payload: CreateDeadlineTypePayload): Promise<DeadlineType> => {
  const response = await api.post('/api/deadline-types', payload)
  return unwrap<DeadlineType>(response.data, 'create deadline type')
}
// PATCH a deadline type's name. Admin-only on the backend (403 for
// non-admin). 409 on duplicate within the firm, 404 cross-firm,
// 200 no-op when the name is unchanged. 400 if a limitation type's new
// name drops "limitation". Renaming changes the type
// across every case using it (firm-wide effect). All rejection
// messages propagate as Error.message via unwrap's throw on !success.
export const updateDeadlineTypeApi = async (typeId: string, payload: UpdateDeadlineTypePayload): Promise<DeadlineType> => {
  const response = await api.patch(`/api/deadline-types/${typeId}`, payload)
  return unwrap<DeadlineType>(response.data, 'update deadline type')
}
// DELETE a deadline type. Admin-only on the backend (403 for
// non-admin). 404 if not found / cross-firm. 409 if ANY CaseDeadline
// still references the type, or it's the firm's only limitation type —
// the reason comes back as Error.message via unwrapVoid's payload.error
// passthrough, so the caller surfaces it inline. The list's
// deleteBlockedReason carries the same reason up front (the Admin panel
// disables Delete with it); the server 409 stays the authority.
export const deleteDeadlineTypeApi = async (typeId: string): Promise<void> => {
  const response = await api.delete(`/api/deadline-types/${typeId}`)
  unwrapVoid(response.data, 'delete deadline type')
}
// ── GRADE RATES API ───────────────────────────────────────
// Per-firm A/B/C/D hourly billing rates. The 4 rows are seeded at
// firm creation (firmController.createFirm) at 0 pence; admin sets
// real rates via PATCH below. List is readable by any firm member
// (fee-earners may need to see rates); only PATCH is admin-gated.
// hourlyRatePence is whole pence — UI converts to/from pounds at
// the edit boundary.
export const listGradeRatesApi = async (): Promise<GradeRate[]> => {
  const response = await api.get('/api/grade-rates')
  return unwrap<GradeRate[]>(response.data, 'load grade rates')
}
// PATCH a grade's hourly rate. Admin-only on the backend (403 for
// non-admins). Only hourlyRatePence is mutable — the grade letter
// is fixed (seeded at firm creation). Backend validates non-negative
// integer pence; rejection surfaces as Error.message via unwrap.
export const updateGradeRateApi = async (id: string, payload: UpdateGradeRatePayload): Promise<GradeRate> => {
  const response = await api.patch(`/api/grade-rates/${id}`, payload)
  return unwrap<GradeRate>(response.data, 'update grade rate')
}
// ── CASE DEADLINES API ────────────────────────────────────
// Per-case deadline rows. List returns deadlineType { id, name }
// embedded inline + ordered by dueDate asc. Mutations are flat at
// /api/case-deadlines/:id; cross-firm requests get 404 (treat as
// "not yours" rather than "doesn't exist").
export const listCaseDeadlinesApi = async (caseId: string): Promise<CaseDeadline[]> => {
  const response = await api.get(`/api/cases/${caseId}/deadlines`)
  return unwrap<CaseDeadline[]>(response.data, 'load case deadlines')
}
// Case-open de-dupe for the deadlines list (see fetchCaseByIdShared).
const inflightDeadlines = new Map<string, Promise<CaseDeadline[]>>()
export const listCaseDeadlinesShared = (caseId: string): Promise<CaseDeadline[]> => {
  const hit = inflightDeadlines.get(caseId)
  if (hit) return hit
  const p = listCaseDeadlinesApi(caseId).finally(() => { inflightDeadlines.delete(caseId) })
  inflightDeadlines.set(caseId, p)
  return p
}
export const createCaseDeadlineApi = async (caseId: string, payload: CreateCaseDeadlinePayload): Promise<CaseDeadline> => {
  const response = await api.post(`/api/cases/${caseId}/deadlines`, payload)
  return unwrap<CaseDeadline>(response.data, 'create case deadline')
}
// Round 2 — the caller's OVERDUE deadlines (dashboard Overdue section) and
// "dismiss" (stop the overdue reminders without completing; refused for
// limitation dates).
// "No limitation date": open cases I can see with no limitation-type
// deadline, each with THE estimate, soonest first.
// assignedToUserId: 'me' | <uuid> → that user's cases (owner OR handling team),
// the same rule as fetchCases — so a count matches the case list's rows.
export const listCasesMissingLimitationApi = async (options?: { assignedToUserId?: string }): Promise<MissingLimitationCase[]> => {
  const response = await api.get('/api/cases/missing-limitation', { params: options?.assignedToUserId ? { assignedToUserId: options.assignedToUserId } : undefined })
  return unwrap<MissingLimitationCase[]>(response.data, 'load cases with no limitation date')
}
// ★ (Audit) Just the NUMBER (?count=1 — one COUNT on the server, no rows): for
// every caller that only shows "N" (the case list's Status option, the
// dashboard). assignedToUserId: 'me' | <uuid> | 'unassigned' (as the list).
export const countCasesMissingLimitationApi = async (options?: { assignedToUserId?: string; claimType?: ClaimTypeFilterValue }): Promise<number> => {
  const response = await api.get('/api/cases/missing-limitation', { params: {
    count: '1',
    ...(options?.assignedToUserId ? { assignedToUserId: options.assignedToUserId } : {}),
    ...(options?.claimType ? { claimType: options.claimType } : {}),
  } })
  return unwrap<{ count: number }>(response.data, 'count cases with no limitation date').count
}
// Admin-only: are the scheduled alert jobs running on this server?
export const getAlertsStatusApi = async (): Promise<{ running: boolean }> => {
  const response = await api.get('/api/system/alerts-status')
  return unwrap<{ running: boolean }>(response.data, 'load alerts status')
}
export const listMyOverdueDeadlinesApi = async (): Promise<OverdueDeadline[]> => {
  const response = await api.get('/api/case-deadlines/overdue')
  return unwrap<OverdueDeadline[]>(response.data, 'load overdue deadlines')
}
export const dismissOverdueDeadlineApi = async (deadlineId: string): Promise<void> => {
  const response = await api.post(`/api/case-deadlines/${deadlineId}/dismiss-overdue`)
  unwrapVoid(response.data, 'dismiss overdue deadline')
}
export const updateCaseDeadlineApi = async (deadlineId: string, payload: UpdateCaseDeadlinePayload & { baseUpdatedAt?: string }): Promise<CaseDeadline> => {
  const response = await api.patch(`/api/case-deadlines/${deadlineId}`, payload)
  return unwrap<CaseDeadline>(response.data, 'update case deadline')
}
export const deleteCaseDeadlineApi = async (deadlineId: string): Promise<void> => {
  const response = await api.delete(`/api/case-deadlines/${deadlineId}`)
  unwrapVoid(response.data, 'delete case deadline')
}
// ── AUDIT LOGS API ────────────────────────────────────────
// GET /api/audit-logs?page=N&limit=N — admin-only (backend 403s
// non-admins; the message propagates as Error.message via the
// unwrap throw). Returns the data array + pagination metadata
// via unwrapPaginated.
export const listAuditLogsApi = async (
  page: number,
  limit: number,
  action?: string,
  actorUserId?: string,
  caseId?: string,
  targetType?: string,
): Promise<PaginatedAuditLogs> => {
  // Conditional filter params — appended only when non-empty so an
  // empty-string default (the "All" sentinel in the UI) coerces to
  // "no filter" rather than a literal empty filter. encodeURIComponent
  // is defensive: current backend action vocabulary (user.role.changed,
  // deadline_type.*) is URL-safe and actorUserId/caseId are CUIDs, but
  // the encode protects future actions / id formats that might contain
  // reserved chars. Switched from a single-ternary URL build to a
  // params[] array-join as the filter set grew past one — the join
  // scales cleanly when more filters are added later (date range, etc.).
  // caseId is the per-case timeline's primary filter (used by
  // CaseHistorySection); action + actorUserId + targetType are the
  // Admin viewer's filters. targetType is the "Category" filter — maps
  // to entry.targetType (task / case / case_deadline / etc.) so the
  // admin can scope the log to a single domain. None of these are
  // mutually exclusive — the backend ANDs them, so passing multiple
  // narrows the result set further.
  const params: string[] = [`page=${page}`, `limit=${limit}`]
  if (action && action.length > 0) {
    params.push(`action=${encodeURIComponent(action)}`)
  }
  if (actorUserId && actorUserId.length > 0) {
    params.push(`actorUserId=${encodeURIComponent(actorUserId)}`)
  }
  if (caseId && caseId.length > 0) {
    params.push(`caseId=${encodeURIComponent(caseId)}`)
  }
  if (targetType && targetType.length > 0) {
    params.push(`targetType=${encodeURIComponent(targetType)}`)
  }
  const url = `/api/audit-logs?${params.join('&')}`
  const response = await api.get(url)
  return unwrapPaginated<AuditLogEntry[]>(response.data, 'list audit logs')
}

// ── DASHBOARD ACTIVITY FEED (self-scoped) ──────────────────
// GET /api/me/activity-feed?page=N&limit=N — the caller's OWN feed:
// events they actioned OR events on cases assigned to them, within their
// firm. Any authenticated member (NOT admin-gated). Same paginated
// envelope + AuditLogEntry rows as listAuditLogsApi, so the renderer
// (renderActionSentence) + types (PaginatedAuditLogs) are reused. The
// admin firm-wide view keeps using listAuditLogsApi (no caseId).
export const listMyActivityFeedApi = async (
  page: number,
  limit: number,
): Promise<PaginatedAuditLogs> => {
  const response = await api.get(`/api/me/activity-feed?page=${page}&limit=${limit}`)
  return unwrapPaginated<AuditLogEntry[]>(response.data, 'load activity feed')
}

// ── NAV-BELL NOTIFICATIONS (self-scoped) ───────────────────
// GET /api/me/notifications?page&limit — the caller's own bell
// notifications, newest-first, with the unread count read off the
// envelope's `unreadCount` sibling (like the activities `total`
// pattern). Plus per-item read, mark-all-read, and dismiss.
export const listNotificationsApi = async (
  page = 1,
  limit = 20,
): Promise<{ data: AppNotification[]; unreadCount: number; pagination: Pagination }> => {
  const response = await api.get(`/api/me/notifications?page=${page}&limit=${limit}`)
  const { data, pagination } = unwrapPaginated<AppNotification[]>(response.data, 'load notifications')
  const raw = response.data as { unreadCount?: unknown }
  const unreadCount = typeof raw.unreadCount === 'number' ? raw.unreadCount : 0
  return { data, unreadCount, pagination }
}
export const markNotificationReadApi = async (id: string): Promise<void> => {
  const response = await api.patch(`/api/me/notifications/${id}/read`)
  unwrapVoid(response.data, 'mark notification read')
}
export const markAllNotificationsReadApi = async (): Promise<void> => {
  const response = await api.post('/api/me/notifications/read-all')
  unwrapVoid(response.data, 'mark all notifications read')
}
export const dismissNotificationApi = async (id: string): Promise<void> => {
  const response = await api.delete(`/api/me/notifications/${id}`)
  unwrapVoid(response.data, 'dismiss notification')
}

// ── CLIENT NOTES (feed) ───────────────────────────────────
// /api/clients/:clientId/notes — see backend clientNoteController.ts.
// List order is server-side: pinned first, newest pin, then newest note.
// Paged: the first call (no cursor) returns ALL pinned notes + the first page
// of unpinned; pass the returned nextCursor to get the next unpinned page
// (no pinned). nextCursor null = nothing more.
export const listClientNotesApi = async (
  clientId: string,
  opts?: { cursor?: string | null; limit?: number },
): Promise<{ notes: ClientNote[]; nextCursor: string | null }> => {
  const params: Record<string, string> = {}
  if (opts?.cursor) params.cursor = opts.cursor
  if (opts?.limit) params.limit = String(opts.limit)
  const response = await api.get(`/api/clients/${clientId}/notes`, { params })
  const notes = unwrap<ClientNote[]>(response.data, 'load notes')
  const nc = (response.data as { nextCursor?: unknown } | undefined)?.nextCursor
  return { notes, nextCursor: typeof nc === 'string' && nc ? nc : null }
}
export const createClientNoteApi = async (clientId: string, body: CreateClientNotePayload): Promise<ClientNote> => {
  const response = await api.post(`/api/clients/${clientId}/notes`, body)
  return unwrap<ClientNote>(response.data, 'add note')
}
// Body edits (content/caseIds/fileIds) are author/admin only; `pinned` alone is open to anyone.
export const updateClientNoteApi = async (clientId: string, noteId: string, body: UpdateClientNotePayload): Promise<ClientNote> => {
  const response = await api.patch(`/api/clients/${clientId}/notes/${noteId}`, body)
  return unwrap<ClientNote>(response.data, 'save note')
}
export const deleteClientNoteApi = async (clientId: string, noteId: string): Promise<void> => {
  const response = await api.delete(`/api/clients/${clientId}/notes/${noteId}`)
  unwrapVoid(response.data, 'delete note')
}

// ── Invoicing Stage 1 — disbursements + standard bills ───────
// All £ are server-computed whole pence; the browser sends selections,
// units, VAT treatments and text — never a total.
export const getCaseBillingApi = async (caseId: string): Promise<CaseBilling> =>
  unwrap<CaseBilling>((await api.get(`/api/cases/${caseId}/billing`)).data, 'load billing')
export const listDisbursementsApi = async (caseId: string): Promise<Disbursement[]> =>
  unwrap<Disbursement[]>((await api.get(`/api/cases/${caseId}/disbursements`)).data, 'load disbursements')
export interface DisbursementInput {
  description?: string; category?: string; amountPence?: number; vatTreatment?: VatTreatment; vatPence?: number
  status?: 'PAID' | 'ANTICIPATED'; payableTo?: string; paidAt?: string
}
export const createDisbursementApi = async (caseId: string, body: DisbursementInput): Promise<Disbursement> =>
  unwrap<Disbursement>((await api.post(`/api/cases/${caseId}/disbursements`, body)).data, 'record disbursement')
export const updateDisbursementApi = async (id: string, body: DisbursementInput): Promise<Disbursement> =>
  unwrap<Disbursement>((await api.patch(`/api/disbursements/${id}`, body)).data, 'update disbursement')
export const deleteDisbursementApi = async (id: string): Promise<void> =>
  unwrapVoid((await api.delete(`/api/disbursements/${id}`)).data, 'delete disbursement')
export interface RaiseBillInput {
  billType: BillType; costsFormat: CostsFormat; activityIds?: string[]; disbursementIds?: string[]
  amountPence?: number; description?: string
}
export const createBillApi = async (caseId: string, body: RaiseBillInput): Promise<Bill> =>
  unwrap<Bill>((await api.post(`/api/cases/${caseId}/invoices`, body)).data, 'create the draft bill')
export const getBillApi = async (id: string): Promise<Bill> => unwrap<Bill>((await api.get(`/api/invoices/${id}`)).data, 'load bill')
export const listBillsApi = async (): Promise<FirmBills> => unwrap<FirmBills>((await api.get('/api/invoices')).data, 'load bills')
// The Activity feed's billing entries for ONE case or ONE client (masked server-side).
// A client statement (on demand — writes nothing). No billIds → every eligible bill (the picker).
export const getClientStatementApi = async (clientId: string, billIds?: string[]): Promise<ClientStatement> =>
  unwrap<ClientStatement>((await api.get(`/api/clients/${clientId}/statement`, { params: billIds ? { billIds: billIds.join(',') } : {} })).data, 'load the statement')
export const listBillEventsApi = async (scope: { caseId: string } | { clientId: string }): Promise<BillEvent[]> =>
  unwrap<BillEvent[]>((await api.get('/api/bill-events', { params: scope })).data, 'load billing activity')
export const updateBillApi = async (id: string, body: { narrative?: string; costsFormat?: CostsFormat; billType?: BillType; paymentTermsDays?: number; periodFrom?: string | null; periodTo?: string | null }): Promise<Bill> =>
  unwrap<Bill>((await api.patch(`/api/invoices/${id}`, body)).data, 'save bill')
export const addBillLineApi = async (id: string, body: { kind: 'PROFIT_COST'; activityId: string } | { kind: 'DISBURSEMENT'; disbursementId: string } | { kind: 'ADJUSTMENT'; description: string; amountPence: number; vatTreatment: VatTreatment }): Promise<Bill> =>
  unwrap<Bill>((await api.post(`/api/invoices/${id}/lines`, body)).data, 'add line')
export const updateBillLineApi = async (id: string, lineId: string, body: { units?: number; amountPence?: number; vatTreatment?: VatTreatment; internalNote?: string; description?: string }): Promise<Bill> =>
  unwrap<Bill>((await api.patch(`/api/invoices/${id}/lines/${lineId}`, body)).data, 'update line')
export const deleteBillLineApi = async (id: string, lineId: string): Promise<Bill> =>
  unwrap<Bill>((await api.delete(`/api/invoices/${id}/lines/${lineId}`)).data, 'remove line')
const billAction = (what: string, verb: string) => async (id: string): Promise<Bill> =>
  unwrap<Bill>((await api.post(`/api/invoices/${id}/${what}`)).data, verb)
// Send for approval, with an optional note to the approving admin(s).
export const submitBillApi = async (id: string, note?: string): Promise<Bill> =>
  unwrap<Bill>((await api.post(`/api/invoices/${id}/submit`, note ? { note } : {})).data, 'send for approval')
export const approveBillApi = billAction('approve', 'approve bill')
export const reopenBillApi = billAction('reopen', 'reopen bill')
// B3 — issue with a signatory (statute bills; default = the case's fee earner).
export const issueBillApi = async (id: string, body: { signatoryUserId?: string } = {}): Promise<Bill> =>
  unwrap<Bill>((await api.post(`/api/invoices/${id}/issue`, body)).data, 'issue bill')
export const getBillSignatoriesApi = async (id: string): Promise<BillSignatories> =>
  unwrap<BillSignatories>((await api.get(`/api/invoices/${id}/signatories`)).data, 'load who can sign')
// B3 — "Mark as sent by post / handed over" (date = YYYY-MM-DD).
export const markBillDeliveredApi = async (id: string, body: { method: 'POST' | 'HAND'; date: string; oldWordingConfirmed?: boolean }): Promise<Bill> =>
  unwrap<Bill>((await api.post(`/api/invoices/${id}/delivered`, body)).data, 'record the delivery')
// B3b — the same for an issued credit note (returns its bill).
export const markCreditNoteDeliveredApi = async (id: string, body: { method: 'POST' | 'HAND'; date: string }): Promise<Bill> =>
  unwrap<Bill>((await api.post(`/api/credit-notes/${id}/delivered`, body)).data, 'record the delivery')
// B3 — Settings → Bill wording (admins only).
export const getBillWordingApi = async (): Promise<BillWordingSettings> =>
  unwrap<BillWordingSettings>((await api.get('/api/firm/bill-wording')).data, 'load the bill wording')
export const patchBillWordingApi = async (body: BillWordingPatch): Promise<BillWordingSettings> =>
  unwrap<BillWordingSettings>((await api.patch('/api/firm/bill-wording', body)).data, 'save the bill wording')
// (Audit fix 1) previewHash = the fingerprint of the text the approver saw
// (GET's previewHash); the server approves only if it's still that text.
export const approveBillWordingApi = async (previewHash: string): Promise<BillWordingSettings> =>
  unwrap<BillWordingSettings>((await api.post('/api/firm/bill-wording/approve', { previewHash })).data, 'approve the bill wording')
export const unapproveBillWordingApi = async (): Promise<BillWordingSettings> =>
  unwrap<BillWordingSettings>((await api.post('/api/firm/bill-wording/unapprove')).data, 'change the approval')
// (Big-audit fix batch 2) The firm's trading name (Firm.name) — admins only.
export const patchFirmNameApi = async (name: string): Promise<{ firmName: string }> =>
  unwrap<{ firmName: string }>((await api.patch('/api/firm/name', { name })).data, 'save the trading name')
// B3 — the client's consent to receive bills by email (date = YYYY-MM-DD).
export interface BillEmailConsentState { id: string; emailAddress: string; hasEmailAddress: boolean | null; billEmailConsentAt: string | null; billEmailConsentEmail: string | null; billEmailConsentMethod: string | null; billEmailConsentRecordedByUserId: string | null; recordedByName?: string | null }
export const getBillEmailConsentApi = async (clientId: string): Promise<BillEmailConsentState> =>
  unwrap<BillEmailConsentState>((await api.get(`/api/clients/${clientId}/bill-email-consent`)).data, 'load the consent')
export const recordBillEmailConsentApi = async (clientId: string, body: { date: string; email: string; method: string }): Promise<BillEmailConsentState> =>
  unwrap<BillEmailConsentState>((await api.put(`/api/clients/${clientId}/bill-email-consent`, body)).data, 'record the consent')
export const removeBillEmailConsentApi = async (clientId: string): Promise<BillEmailConsentState> =>
  unwrap<BillEmailConsentState>((await api.delete(`/api/clients/${clientId}/bill-email-consent`)).data, 'remove the consent')
// Restricted copy (a viewer who sees only their own £): a short-lived signed
// link to the server's redacted render of this bill's PDF — opened / fetched
// like a presigned file URL (utils/documentAccess).
export const getRedactedBillPdfLinkApi = async (id: string, inline = false): Promise<{ downloadUrl: string; filename: string }> =>
  unwrap<{ downloadUrl: string; filename: string }>((await api.get(`/api/invoices/${id}/pdf-link`, { params: inline ? { inline: 1 } : {} })).data, 'prepare the restricted PDF')
// Overlapping drafts: drop every line another bill has already billed.
export const removeBilledLinesApi = billAction('remove-billed-lines', 'remove billed lines')
// "Update to current": re-pull a changed time line's figures from its entry.
export const refreshBillLineApi = async (id: string, lineId: string): Promise<Bill> =>
  unwrap<Bill>((await api.post(`/api/invoices/${id}/lines/${lineId}/refresh`)).data, 'update line')
// Approver: return a bill sent for approval (or approved) to DRAFT with a note.
export const sendBackBillApi = async (id: string, reason: string): Promise<Bill> =>
  unwrap<Bill>((await api.post(`/api/invoices/${id}/send-back`, { reason })).data, 'send bill back')
// Admin: reverse a payment recorded in error (kept on record as reversed).
export const reverseBillPaymentApi = async (id: string, paymentId: string, reason: string): Promise<Bill> =>
  unwrap<Bill>((await api.post(`/api/invoices/${id}/payments/${paymentId}/reverse`, { reason })).data, 'reverse payment')
export const recordBillPaymentApi = async (id: string, body: { amountPence: number; method?: string; receivedAt?: string }): Promise<Bill> =>
  unwrap<Bill>((await api.post(`/api/invoices/${id}/payments`, body)).data, 'record payment')
export const deleteBillApi = async (id: string): Promise<void> => unwrapVoid((await api.delete(`/api/invoices/${id}`)).data, 'delete draft')
// Invoicing Stage 2 — credit notes + write-off. Every call returns the bill
// (with its credit notes) recomputed by the server.
export interface CreditNoteInput { mode: CreditNoteMode; amountPence?: number; lineIds?: string[]; reason: string; releaseToUnbilled?: boolean }
export const previewCreditNoteApi = async (billId: string, body: Omit<CreditNoteInput, 'reason'> & { reason?: string }): Promise<CreditNotePreview> =>
  unwrap<CreditNotePreview>((await api.post(`/api/invoices/${billId}/credit-notes`, { ...body, preview: true })).data, 'preview credit note')
export const createCreditNoteApi = async (billId: string, body: CreditNoteInput): Promise<Bill> =>
  unwrap<Bill>((await api.post(`/api/invoices/${billId}/credit-notes`, body)).data, 'draft credit note')
export const issueCreditNoteApi = async (id: string): Promise<Bill> => unwrap<Bill>((await api.post(`/api/credit-notes/${id}/issue`)).data, 'issue credit note')
export const deleteCreditNoteApi = async (id: string): Promise<Bill> => unwrap<Bill>((await api.delete(`/api/credit-notes/${id}`)).data, 'delete credit note')
export const writeOffBillApi = async (id: string, reason: string): Promise<Bill> => unwrap<Bill>((await api.post(`/api/invoices/${id}/write-off`, { reason })).data, 'write off balance')
// Admin: void an ON-ACCOUNT request issued in error (no payments on it). It keeps its number.
export const voidBillApi = async (id: string, reason: string): Promise<Bill> => unwrap<Bill>((await api.post(`/api/invoices/${id}/void`, { reason })).data, 'void request')
// Send bill — email an issued bill's PDF to the client (Microsoft Graph).
// The draft is the firm's template with merge fields already resolved.
export interface BillSendDraft {
  to: string
  clientHasEmail: boolean
  // (B4 follow-up 2) A child / protected party → their litigation friend.
  recipient?: SendRecipient
  // B3 — delivery rules (the server enforces them on send too).
  noEmailAddress?: boolean
  wordingReady?: boolean
  wordingProblem?: string | null
  issuedBeforeWording?: boolean
  consent?: { at: string; email: string | null; method: string | null } | null
  consentChanged?: boolean
  emailAllowed?: boolean
  emailBlockedReason?: string | null
  clientEmail?: string | null
  // B3b — the client record ("Record consent" in the send dialog).
  clientId?: string
  clientName: string
  senderEmail: string
  subject: string
  body: string
  attachmentName: string
  delivery: Bill['delivery']
  // (Audit fix batch 1, Low 9) The PDF names a different litigation friend from today's.
  addresseeWarning?: string | null
}
export const getBillSendDraftApi = async (id: string): Promise<BillSendDraft> =>
  unwrap<BillSendDraft>((await api.get(`/api/invoices/${id}/send-draft`)).data, 'prepare the email')
// (Audit fix batch 1, 31 Oct) idempotencyKey: one per send window, repeated
// on a retry — the server sends the email once (a retry gets the first reply).
export interface SendBody { to: string; ccMe: boolean; subject: string; body: string; idempotencyKey?: string }
export interface BillSendSuccess { bill: Bill | null; to: string[]; cc: string[]; firstDelivery: boolean; deliveryRecorded: boolean; activityLogged: boolean; pdfSaved: boolean }
// Like sendOutlookMailApi: connect / reconnect / field errors come back as
// data (not a throw) so the modal keeps everything the user typed.
export type BillSendResult =
  | { ok: true; data: BillSendSuccess }
  | { ok: false; needsConnect?: boolean; needsReconnect?: boolean; field?: 'to' | 'subject' | 'body'; error: string }
// B3b — an issued credit note's send draft (same shape as a bill's).
export const getCreditNoteSendDraftApi = async (id: string): Promise<BillSendDraft> =>
  unwrap<BillSendDraft>((await api.get(`/api/credit-notes/${id}/send-draft`)).data, 'prepare the email')
export const sendCreditNoteApi = async (id: string, body: SendBody): Promise<BillSendResult> =>
  postSend(`/api/credit-notes/${id}/send`, body, 'credit note')
// (Follow-ups) The send window's first step ("Preparing PDF…"): the bill PDF
// is made ready on the server; the send that follows reuses it.
export const prepareBillSendApi = async (id: string): Promise<{ ready: boolean; timings?: Record<string, number> }> =>
  unwrap((await api.post(`/api/invoices/${id}/send/prepare`, {})).data, 'prepare the bill PDF')
export const sendBillApi = async (id: string, body: SendBody): Promise<BillSendResult> =>
  postSend(`/api/invoices/${id}/send`, body, 'bill')
const postSend = async (url: string, body: SendBody, what: 'bill' | 'credit note'): Promise<BillSendResult> => {
  try {
    return { ok: true, data: unwrap<BillSendSuccess>((await api.post(url, body)).data, `send ${what}`) }
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.data && typeof err.response.data === 'object') {
      const d = err.response.data as { needsConnect?: boolean; needsReconnect?: boolean; field?: string; error?: string }
      const field = d.field === 'to' || d.field === 'subject' || d.field === 'body' ? d.field : undefined
      return { ok: false, needsConnect: d.needsConnect, needsReconnect: d.needsReconnect, field, error: d.error ?? `Could not send the ${what}. Please try again.` }
    }
    return { ok: false, error: `Could not send the ${what} — check your connection, then try again. (If it was sent, it is in the case history.)` }
  }
}

export default api

// ── STAGE 4 ROUND 2a — PI SETTLEMENT INPUTS + PREVIEW ──────────
// backend routes/settlementRoutes.ts. Every case route needs case access AND
// "See colleagues' billing" (or admin); GET answers { locked: true } instead
// of the figures when the permission is missing. The preview writes nothing.
export const getSettlementApi = async (caseId: string): Promise<SettlementState> =>
  unwrap<SettlementState>((await api.get(`/api/cases/${caseId}/settlement`)).data, 'load the settlement')
export const previewSettlementApi = async (caseId: string, body: { edits?: PreviewEdits; options?: SettlementPreviewOptions }): Promise<PreviewResult> =>
  unwrap<PreviewResult>((await api.post(`/api/cases/${caseId}/settlement/preview`, body)).data, 'preview the settlement')
// Field-level autosave; 409 { error: 'stale', currentUpdatedAt } when someone saved since baseUpdatedAt.
export const patchSettlementMatterApi = async (caseId: string, fields: Partial<MatterValues>, baseUpdatedAt?: string | null): Promise<{ values: MatterValues; updatedAt: string }> =>
  unwrap((await api.patch(`/api/cases/${caseId}/settlement/matter`, { ...fields, ...(baseUpdatedAt ? { baseUpdatedAt } : {}) })).data, 'save the settlement details')
export const confirmClientAdultApi = async (caseId: string): Promise<{ adultConfirmedAt: string; updatedAt: string }> =>
  unwrap((await api.post(`/api/cases/${caseId}/settlement/confirm-adult`)).data, 'confirm the client is an adult')
export const createCfaApi = async (caseId: string): Promise<{ id: string }> =>
  unwrap((await api.post(`/api/cases/${caseId}/settlement/cfa`)).data, 'create the CFA')
export const updateCfaApi = async (caseId: string, terms: Partial<CfaEditable>, baseUpdatedAt?: string): Promise<{ updatedAt: string; changed: string[] }> =>
  unwrap((await api.patch(`/api/cases/${caseId}/settlement/cfa`, { ...terms, ...(baseUpdatedAt ? { baseUpdatedAt } : {}) })).data, 'save the CFA')
export const reviewCfaApi = async (caseId: string): Promise<{ reviewedAt: string; updatedAt: string }> =>
  unwrap((await api.post(`/api/cases/${caseId}/settlement/cfa/review`)).data, 'mark the CFA reviewed')
export const putBaseCostsApi = async (caseId: string, body: { netPence: number; vatPence: number; reason: string } | { clear: true }): Promise<{ baseCostsOverride: BaseCostsOverride | null }> =>
  unwrap((await api.put(`/api/cases/${caseId}/settlement/base-costs`, body)).data, 'save the base costs')
export const createInterimApi = async (caseId: string, body: { amountPence: number; receivedOn: string; payer?: string; note?: string }): Promise<InterimPayment> =>
  unwrap((await api.post(`/api/cases/${caseId}/settlement/interims`, body)).data, 'add the interim payment')
export const updateInterimApi = async (caseId: string, id: string, body: Partial<{ amountPence: number; receivedOn: string; payer: string; note: string }>): Promise<InterimPayment> =>
  unwrap((await api.patch(`/api/cases/${caseId}/settlement/interims/${id}`, body)).data, 'save the interim payment')
export const deleteInterimApi = async (caseId: string, id: string): Promise<void> =>
  unwrapVoid((await api.delete(`/api/cases/${caseId}/settlement/interims/${id}`)).data, 'remove the interim payment')
export const patchDisbursementRecoveryApi = async (caseId: string, id: string, body: Partial<{ recoveryStatus: RecoveryStatus; recoveredPence: number; recoveredVatPence: number; recoveredAt: string | null }>): Promise<SettlementDisbursement> =>
  unwrap((await api.patch(`/api/cases/${caseId}/settlement/disbursements/${id}`, body)).data, 'save the recovery')
// (B4 follow-up) The Costs / ATE card in ONE request, all-or-nothing on the
// server (its figures + the override, or its figures + each recovery).
export const saveSettlementCardApi = async (caseId: string, card: SettlementCardKind, body: SettlementCardSave): Promise<SettlementCardSaved> =>
  unwrap((await api.put(`/api/cases/${caseId}/settlement/${card}`, body)).data, 'save the card')
// Admin › PI settlements (admin only).
export const getSettlementDefaultsApi = async (): Promise<SettlementDefaults> =>
  unwrap((await api.get('/api/firm/pi-settlement-defaults')).data, 'load the PI settlement defaults')
export const patchSettlementDefaultsApi = async (patch: Partial<SettlementDefaults>): Promise<SettlementDefaults> =>
  unwrap((await api.patch('/api/firm/pi-settlement-defaults', patch)).data, 'save the PI settlement defaults')

// ── B4 — settlement / completion statements ──────────────────
// Every write answers with the case's statements as they now are. A refused
// issue is a 409 carrying `blockers` (axios error → response.data.blockers).
const stPath = (caseId: string, id?: string, tail = ''): string => `/api/cases/${caseId}/settlement/statements${id ? `/${id}` : ''}${tail}`
export const getCaseStatementsApi = async (caseId: string): Promise<CaseStatements> =>
  unwrap<CaseStatements>((await api.get(stPath(caseId))).data, 'load the settlement statements')
export const saveStatementDraftApi = async (caseId: string, body: DraftFields): Promise<CaseStatements> =>
  unwrap<CaseStatements>((await api.put(stPath(caseId, undefined, '/draft'), body)).data, 'save the statement')
export const prepareStatementApi = async (caseId: string, id: string): Promise<CaseStatements> =>
  unwrap<CaseStatements>((await api.post(stPath(caseId, id, '/prepare'))).data, 'mark the statement as prepared')
// (B4 follow-up) With advisory notes the approver ticks "I've reviewed these
// notes" — the codes they saw (a change meanwhile → 409). (Audit fix H1) It
// also sends the fingerprint of the figures shown: it approves exactly those.
// (Batch 2 Low) notes = the notes shown (code + wording): a note whose wording
// changed meanwhile is re-reviewed (409).
export const checkStatementApi = async (caseId: string, id: string, fingerprint: string | undefined, review?: { notesReviewed: boolean; noteCodes: string[]; notes?: { code: string; message: string }[] }): Promise<CaseStatements> =>
  unwrap<CaseStatements>((await api.post(stPath(caseId, id, '/check'), { ...(review ?? {}), fingerprint })).data, 'approve the statement')
// "Ask [names] to approve": bell + email to the firm's other approvers.
export const askStatementApprovalApi = async (caseId: string, id: string): Promise<{ asked: string[] }> =>
  unwrap<{ asked: string[] }>((await api.post(stPath(caseId, id, '/ask-approval'))).data, 'ask for approval')
// (B4 follow-ups) The litigation friend + deputy / attorney + their email
// consent — Client details (case access).
export const getLitigationFriendApi = async (caseId: string): Promise<LitigationFriendState> =>
  unwrap<LitigationFriendState>((await api.get(`/api/cases/${caseId}/litigation-friend`)).data, 'load the litigation friend')
export const putLitigationFriendApi = async (caseId: string, body: Partial<Representatives>): Promise<LitigationFriendState> =>
  unwrap<LitigationFriendState>((await api.put(`/api/cases/${caseId}/litigation-friend`, body)).data, 'save the litigation friend')
// Answers in the bill-consent shape too, so the send windows' inline form can use it.
export const recordLitigationFriendConsentApi = async (caseId: string, body: { date: string; email: string; method: string }): Promise<LitigationFriendState & BillEmailConsentState> =>
  unwrap((await api.put(`/api/cases/${caseId}/litigation-friend/email-consent`, body)).data, 'record the consent')
export const removeLitigationFriendConsentApi = async (caseId: string): Promise<LitigationFriendState> =>
  unwrap<LitigationFriendState>((await api.delete(`/api/cases/${caseId}/litigation-friend/email-consent`)).data, 'remove the consent')
// B4c (D4) The history of a mobile number used for e-signature codes (masked
// values only; newest first). Firm-scoped server-side.
export const getClientMobileChangesApi = async (clientId: string): Promise<MobileChange[]> =>
  unwrap<MobileChange[]>((await api.get(`/api/clients/${clientId}/mobile-changes`)).data, 'load the mobile number history')
export const getLitigationFriendMobileChangesApi = async (caseId: string): Promise<MobileChange[]> =>
  unwrap<MobileChange[]>((await api.get(`/api/cases/${caseId}/litigation-friend/mobile-changes`)).data, 'load the mobile number history')
export const getWitnessMobileChangesApi = async (witnessId: string): Promise<MobileChange[]> =>
  unwrap<MobileChange[]>((await api.get(`/api/cases/witnesses/${witnessId}/mobile-changes`)).data, 'load the mobile number history')
// (Audit fix batch 1, H2) mismatchOverride: "I've checked the difference" with
// the final bill + a reason (a mismatch without it → 409 BILL_MISMATCH).
export const issueStatementApi = async (caseId: string, id: string, mismatchOverride?: MismatchOverrideBody): Promise<CaseStatements> =>
  unwrap<CaseStatements>((await api.post(stPath(caseId, id, '/issue'), mismatchOverride ? { mismatchOverride } : {})).data, 'issue the statement')
// (Audit B2, low b) A difference with the final bill: "I've checked the
// difference" + the reason (for THIS difference — key), then an approver OKs it.
export const checkStatementMismatchApi = async (caseId: string, id: string, body: { checked: true; reason: string; key: string }): Promise<CaseStatements> =>
  unwrap<CaseStatements>((await api.post(stPath(caseId, id, '/mismatch-check'), body)).data, 'record the check')
export const approveStatementMismatchApi = async (caseId: string, id: string, key: string): Promise<CaseStatements> =>
  unwrap<CaseStatements>((await api.post(stPath(caseId, id, '/mismatch-approve'), { key })).data, 'OK the difference')
export const correctStatementApi = async (caseId: string, id: string): Promise<CaseStatements> =>
  unwrap<CaseStatements>((await api.post(stPath(caseId, id, '/correct'))).data, 'start a corrected version')
export const recordCourtOrderApi = async (caseId: string, id: string, body: CourtOrderBody): Promise<CaseStatements> =>
  unwrap<CaseStatements>((await api.put(stPath(caseId, id, '/court-order'), body)).data, 'record the court order')
export const recordCourtStageApi = async (caseId: string, id: string, body: { stage: CourtStage; on: string; note?: string }): Promise<CaseStatements> =>
  unwrap<CaseStatements>((await api.post(stPath(caseId, id, '/court-stage'), body)).data, 'record the court step')
export const recordClientApprovalApi = async (caseId: string, id: string, body: ClientApprovalBody): Promise<CaseStatements> =>
  unwrap<CaseStatements>((await api.post(stPath(caseId, id, '/client-approval'), body)).data, 'record the client’s answer')
// (Big-audit batch 2, M1) A later instruction after an e-signed answer: recorded, then approved / refused by another approver.
export const recordLaterInstructionApi = async (caseId: string, id: string, body: LaterInstructionBody): Promise<CaseStatements> =>
  unwrap<CaseStatements>((await api.post(stPath(caseId, id, '/later-instructions'), body)).data, 'record the later instruction')
export const approveLaterInstructionApi = async (caseId: string, id: string, instructionId: string): Promise<CaseStatements> =>
  unwrap<CaseStatements>((await api.post(stPath(caseId, id, `/later-instructions/${encodeURIComponent(instructionId)}/approve`))).data, 'approve the later instruction')
export const refuseLaterInstructionApi = async (caseId: string, id: string, instructionId: string, reason: string): Promise<CaseStatements> =>
  unwrap<CaseStatements>((await api.post(stPath(caseId, id, `/later-instructions/${encodeURIComponent(instructionId)}/refuse`), { reason })).data, 'refuse the later instruction')
export const getStatementDocumentApi = async (caseId: string, id: string): Promise<{ doc: StatementDocument; status: StatementStatus; contentHash: string | null }> =>
  unwrap((await api.get(stPath(caseId, id, '/document'))).data, 'load the statement')
export const getStatementSendDraftApi = async (caseId: string, id: string): Promise<StatementSendDraft> =>
  unwrap<StatementSendDraft>((await api.get(stPath(caseId, id, '/send-draft'))).data, 'prepare the email')
// (Batch 2) withBillId: the final bill goes in the same email (both PDFs
// frozen, delivery recorded on both, the statement linked to the bill).
// (Follow-ups) "Preparing PDFs…": the statement (+ the pack's bill) PDFs made
// ready on the server; nothing is sent.
export const prepareStatementSendApi = async (caseId: string, id: string, body: { withBillId?: string }): Promise<{ ready: boolean; timings?: Record<string, number> }> =>
  unwrap((await api.post(stPath(caseId, id, '/send/prepare'), body)).data, 'prepare the PDFs')
// (Audit fix batch 1) idempotencyKey (safe to retry); mismatchOverride (H2).
export const sendStatementApi = async (caseId: string, id: string, body: { to: string; subject: string; body: string; ccMe?: boolean; withBillId?: string; idempotencyKey?: string; mismatchOverride?: MismatchOverrideBody }): Promise<{ to: string[]; firstDelivery: boolean; activityLogged: boolean; pdfSaved: boolean; replayed?: boolean; timings?: Record<string, number> }> =>
  unwrap((await api.post(stPath(caseId, id, '/send'), body)).data, 'send the statement')
// (Audit fix batch 1) billAlreadySent: the bill already reached the client and
// the user ticked "Record it again" deliberately; mismatchOverride (H2, Low 15).
export const markStatementDeliveredApi = async (caseId: string, id: string, body: { method: 'POST' | 'HAND'; date: string; withBillId?: string; billAlreadySent?: boolean; mismatchOverride?: MismatchOverrideBody }): Promise<CaseStatements> =>
  unwrap<CaseStatements>((await api.post(stPath(caseId, id, '/delivered'), body)).data, 'record the delivery')
// (Batch 2) "Send back for changes": withdraws the preparation / approval of a
// draft, with a required reason (audited) — the figures unlock.
export const sendBackStatementApi = async (caseId: string, id: string, reason: string): Promise<CaseStatements> =>
  unwrap<CaseStatements>((await api.post(stPath(caseId, id, '/send-back'), { reason })).data, 'send the statement back for changes')
// (Batch 2 / audit M12) Paged on the server, newest first: nextCursor = more exist.
export const getStatementRegisterApi = async (opts: { includeDrafts?: boolean; limit?: number; cursor?: string | null } = {}): Promise<RegisterPage> =>
  unwrap<RegisterPage>((await api.get('/api/settlement-statements', { params: { ...(opts.includeDrafts ? { includeDrafts: 'true' } : {}), ...(opts.limit ? { limit: opts.limit } : {}), ...(opts.cursor ? { cursor: opts.cursor } : {}) } })).data, 'load the settlement register')
export const getSettlementWordingApi = async (): Promise<SettlementWordingState> =>
  unwrap<SettlementWordingState>((await api.get('/api/firm/settlement-wording')).data, 'load the settlement wording')
export const patchSettlementWordingApi = async (body: SettlementWordingPatch): Promise<SettlementWordingState> =>
  unwrap<SettlementWordingState>((await api.patch('/api/firm/settlement-wording', body)).data, 'save the settlement wording')
export const approveSettlementWordingApi = async (previewHash: string): Promise<SettlementWordingState> =>
  unwrap<SettlementWordingState>((await api.post('/api/firm/settlement-wording/approve', { previewHash })).data, 'approve the settlement wording')
export const unapproveSettlementWordingApi = async (): Promise<SettlementWordingState> =>
  unwrap<SettlementWordingState>((await api.post('/api/firm/settlement-wording/unapprove')).data, 'change the approval')

// B3 — a member's job title (printed when they sign a bill). Admin-only.
export const updateUserJobTitleApi = async (userId: string, jobTitle: string | null): Promise<{ id: string; jobTitle: string | null }> =>
  unwrap<{ id: string; jobTitle: string | null }>((await api.patch(`/api/users/${userId}/job-title`, { jobTitle })).data, 'update the job title')

// ── B4c E-SIGNATURE (batch 1a) ──────────────────────────────
// Staff (admin-only settings; signed in like every other call).
export const getEsignSettingsApi = async (): Promise<EsignSettings> =>
  unwrap<EsignSettings>((await api.get('/api/esign/settings')).data, 'load the e-signature settings')
// (E-sign audit fixes A) Switching the pilot off answers linksRevoked / revokeFailed too.
export const patchEsignSettingsApi = async (body: EsignSettingsPatch): Promise<EsignSettingsSaved> =>
  unwrap<EsignSettingsSaved>((await api.patch('/api/esign/settings', body)).data, 'save the e-signature settings')
// `version` = consent.version as read — a 409 if the wording changed since.
export const approveEsignConsentApi = async (version: string): Promise<EsignSettings> =>
  unwrap<EsignSettings>((await api.post('/api/esign/settings/consent/approve', { version })).data, 'approve the consent wording')
export const unapproveEsignConsentApi = async (): Promise<EsignSettings> =>
  unwrap<EsignSettings>((await api.post('/api/esign/settings/consent/unapprove')).data, 'change the approval')
export const listSigningRequestsApi = async (caseId?: string): Promise<SigningRequestSummary[]> =>
  unwrap<SigningRequestSummary[]>((await api.get('/api/esign/requests', { params: caseId ? { caseId } : {} })).data, 'load the signing requests')
export const getSigningRequestApi = async (id: string): Promise<SigningRequestDetail> =>
  unwrap<SigningRequestDetail>((await api.get(`/api/esign/requests/${encodeURIComponent(id)}`)).data, 'load the signing request')
export const revokeSigningRequestApi = async (id: string, reason: string): Promise<{ id: string; status: 'REVOKED' }> =>
  unwrap<{ id: string; status: 'REVOKED' }>((await api.post(`/api/esign/requests/${encodeURIComponent(id)}/revoke`, { reason })).data, 'cancel the signing request')
// (batch 1b) Statements: the e-signature checklist / send / resend / cancel; the evidence pack; the explainer.
export const getStatementEsignApi = async (caseId: string, id: string): Promise<StatementEsign> =>
  unwrap<StatementEsign>((await api.get(`/api/cases/${caseId}/settlement/statements/${id}/esign`)).data, 'load the e-signature details')
export const sendStatementForEsignApi = async (caseId: string, id: string, idempotencyKey: string): Promise<{ requestId: string; reference: string; via: 'OUTLOOK' | 'SYSTEM'; to: string }> =>
  unwrap((await api.post(`/api/cases/${caseId}/settlement/statements/${id}/esign/send`, { idempotencyKey })).data, 'send it for e-signature')
// (E-sign audit fixes A) The second staff check when the signer's contact details changed recently.
export const requestEsignSecondCheckApi = async (caseId: string, id: string): Promise<EsignContactCheck | null> =>
  unwrap<{ contactCheck: EsignContactCheck | null }>((await api.post(`/api/cases/${caseId}/settlement/statements/${id}/esign/second-check`)).data, 'ask for a second check').contactCheck
export const approveEsignSecondCheckApi = async (caseId: string, id: string, approvalId: string): Promise<EsignContactCheck | null> =>
  unwrap<{ contactCheck: EsignContactCheck | null }>((await api.post(`/api/cases/${caseId}/settlement/statements/${id}/esign/second-check/approve`, { approvalId })).data, 'confirm the details').contactCheck
// (D12) The colleague doesn't confirm the details — the reason is required (≤ 500).
export const refuseEsignSecondCheckApi = async (caseId: string, id: string, approvalId: string, reason: string): Promise<EsignContactCheck | null> =>
  unwrap<{ contactCheck: EsignContactCheck | null }>((await api.post(`/api/cases/${caseId}/settlement/statements/${id}/esign/second-check/refuse`, { approvalId, reason })).data, 'record that the details weren’t confirmed').contactCheck
export const resendStatementEsignApi = async (caseId: string, id: string): Promise<{ requestId: string; via: 'OUTLOOK' | 'SYSTEM'; linkExpiresAt: string }> =>
  unwrap((await api.post(`/api/cases/${caseId}/settlement/statements/${id}/esign/resend`, {})).data, 'resend the link')
export const cancelStatementEsignApi = async (caseId: string, id: string, reason: string): Promise<void> => {
  unwrapVoid((await api.post(`/api/cases/${caseId}/settlement/statements/${id}/esign/cancel`, { reason })).data, 'cancel the e-signature request')
}
// (E-sign fixes B) A refused BLOB request's body is a Blob too, so the
// server's `{ error, code }` (ESIGN_OFF, SEAL_NOT_SET_UP …) was never read and
// the screen said "Couldn’t make the PDF" / a bare 404. Read the JSON out of
// the error blob into `response.data` (+ the message) so errorText(e) shows
// the reason; anything that isn't JSON is left as it was. Exported for tests.
const readBlobText = (b: Blob): Promise<string> =>
  typeof b.text === 'function' ? b.text() : new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(String(r.result ?? '')); r.onerror = () => reject(r.error)
    r.readAsText(b)
  })
export const withBlobErrorReason = async (e: unknown): Promise<never> => {
  const res = (e as { response?: { data?: unknown } } | null)?.response
  if (res && typeof Blob !== 'undefined' && res.data instanceof Blob) {
    try {
      const j: unknown = JSON.parse(await readBlobText(res.data))
      if (j && typeof j === 'object') {
        res.data = j
        const msg = (j as { error?: unknown }).error
        if (typeof msg === 'string' && msg && e instanceof Error) e.message = msg
      }
    } catch { /* not JSON — leave it */ }
  }
  throw e
}
const getBlobApi = async (url: string): Promise<Blob> => {
  try { return (await api.get(url, { responseType: 'blob' })).data as Blob } catch (e) { return withBlobErrorReason(e) }
}
// (D12) redacted: IP addresses, devices and reasons removed (for the other side).
export const getEvidencePackApi = async (requestId: string, opts: { redacted?: boolean } = {}): Promise<Blob> =>
  getBlobApi(`/api/esign/requests/${encodeURIComponent(requestId)}/evidence-pack${opts.redacted ? '?redacted=1' : ''}`)
export const getEsignExplainerPdfApi = async (): Promise<Blob> =>
  getBlobApi('/api/esign/explainer.pdf')
export const approveEsignExplainerApi = async (version: string): Promise<EsignSettings> =>
  unwrap<EsignSettings>((await api.post('/api/esign/settings/explainer/approve', { version })).data, 'approve the statement')
export const unapproveEsignExplainerApi = async (): Promise<EsignSettings> =>
  unwrap<EsignSettings>((await api.post('/api/esign/settings/explainer/unapprove')).data, 'change the approval')
export const getSigningEvidenceApi = async (id: string, evidenceId: string): Promise<Blob> =>
  getBlobApi(`/api/esign/requests/${encodeURIComponent(id)}/evidence/${encodeURIComponent(evidenceId)}`)

// PUBLIC (no login — the verify page and a signer's link). Their own axios
// instance: no session token is sent and a 401 here can never sign a
// colleague out. Errors carry the server's { error } on err.response.data.
const publicApi = axios.create({ baseURL: BASE_URL })
export const verifyByRefApi = async (ref: string): Promise<VerifyByRefResult> =>
  unwrap<VerifyByRefResult>((await publicApi.get(`/api/verify/${encodeURIComponent(ref)}`)).data, 'check that reference')
// The PDF goes up as the raw request body (not a form) — the server reads
// application/pdf, max 25 MB, and keeps nothing.
export const verifyUploadApi = async (file: File | Blob): Promise<VerifyUploadResult> =>
  unwrap<VerifyUploadResult>((await publicApi.post('/api/verify', file, { headers: { 'Content-Type': 'application/pdf' } })).data, 'check that file')
// (E-sign audit fixes A) The seal certificate, published — its facts, and the file itself.
export const getSealCertificateApi = async (): Promise<SealCertificateInfo> =>
  unwrap<SealCertificateInfo>((await publicApi.get('/api/verify/seal')).data, 'load the seal certificate')
export const SEAL_CERTIFICATE_URL = `${BASE_URL}/api/verify/seal.cer`
// (batch 1b) The signing page. After the code, the short session travels in
// X-Esign-Session (never a cookie, never the app's login).
// (E-sign audit fixes A) The link's token is NEVER in a request URL: it is
// POSTed once (in the body) to /api/sign/open for a link HANDLE, sent after
// that in X-Esign-Link to /api/sign/link/… (a 404 = the handle ran out or the
// link changed — the page opens again).
const linkHeaders = (handle: string, session?: string | null): Record<string, string> => ({ 'X-Esign-Link': handle, ...(session ? { 'X-Esign-Session': session } : {}) })
const LINK = '/api/sign/link'
export const openSignerLinkApi = async (token: string, session?: string | null): Promise<{ handle: string; view: SignerLinkLookup }> =>
  unwrap((await publicApi.post('/api/sign/open', { token }, { headers: session ? { 'X-Esign-Session': session } : {} })).data, 'open that link')
export const lookupSignerLinkApi = async (handle: string, session?: string | null): Promise<SignerLinkLookup> =>
  unwrap<SignerLinkLookup>((await publicApi.get(LINK, { headers: linkHeaders(handle, session) })).data, 'open that link')
export const sendSignCodeApi = async (handle: string): Promise<{ maskedTo: string; expiresAt: string; resendsLeft: number }> =>
  unwrap((await publicApi.post(`${LINK}/code`, {}, { headers: linkHeaders(handle) })).data, 'send the code')
export const verifySignCodeApi = async (handle: string, code: string): Promise<{ session: string; view: SignerLinkLookup | null }> =>
  unwrap((await publicApi.post(`${LINK}/verify`, { code }, { headers: linkHeaders(handle) })).data, 'check the code')
export const giveSignConsentApi = async (handle: string, session: string, version: string): Promise<void> => {
  unwrapVoid((await publicApi.post(`${LINK}/consent`, { agree: true, version }, { headers: linkHeaders(handle, session) })).data, 'record your agreement')
}
export const getSignDocumentApi = async (handle: string, session: string): Promise<ArrayBuffer> =>
  (await publicApi.get(`${LINK}/document`, { headers: linkHeaders(handle, session), responseType: 'arraybuffer' })).data as ArrayBuffer
// (E-sign fixes B) + how they read it: SCROLL (reached the end), BUTTON ("I’ve read the whole document", once the
// last page was served and drawn) or DOWNLOAD ("I’ve read all N pages of the downloaded copy").
export const markSignViewedApi = async (handle: string, session: string, sha256: string, read?: { pages: number; method: SignReadMethod }): Promise<void> => {
  unwrapVoid((await publicApi.post(`${LINK}/viewed`, { sha256, ...(read ?? {}) }, { headers: linkHeaders(handle, session) })).data, 'record that you read it')
}
// replayed: the same Sign press arrived twice (a retry) — it counts as signed.
// (E-sign fixes B) Answers at once ({ sealed, sealing, answerOutcome }); the sealed copy follows.
export const signDocumentApi = async (handle: string, session: string, body: SignBody): Promise<SignResult> =>
  unwrap((await publicApi.post(`${LINK}/sign`, body, { headers: linkHeaders(handle, session) })).data, 'sign')
export const declineSignApi = async (handle: string, session: string | null, body: { kind: SignDeclineKind; reason?: string }): Promise<void> => {
  unwrapVoid((await publicApi.post(`${LINK}/decline`, body, { headers: linkHeaders(handle, session) })).data, 'record that')
}
// (E-sign fixes B) Keep the code session alive (warned 2 minutes before it ends; refreshed on activity at most every
// 5 minutes). Capped by the server at 2 hours after the code was entered.
export const refreshSignSessionApi = async (handle: string, session: string): Promise<SignSessionRefresh> =>
  unwrap<SignSessionRefresh>((await publicApi.post(`${LINK}/session`, {}, { headers: linkHeaders(handle, session) })).data, 'keep you signed in')
// null while the seal is still being made (the server answers 202 { pending }).
export const getSignedCopyApi = async (handle: string, session: string): Promise<Blob | null> => {
  const r = await publicApi.get(`${LINK}/copy`, { headers: linkHeaders(handle, session), responseType: 'blob', validateStatus: (st) => st === 200 || st === 202 })
  return r.status === 200 ? (r.data as Blob) : null
}

// ── (D12 live updates) the fallback while the live socket is down ──
// When the visible case (?caseId=) / the firm's lists last changed. Polled
// every 30 s while the tab is visible (hooks/useLiveTopics); a moved `at` →
// refetch what's on screen. Case access as the case page.
export interface LiveStampRow { key: string; at: string; topics: string[] }
export interface LiveStamp { case: LiveStampRow | null; firm: LiveStampRow | null }
export const getLiveStampApi = async (caseId?: string | null): Promise<LiveStamp> =>
  unwrap<LiveStamp>((await api.get('/api/live/stamp', { params: caseId ? { caseId } : {} })).data, 'check for changes')
